// ImageResults.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { useUser } from "../../../context/UserContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  PieChart,
  Pie,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { getAuthToken } from "../../../utils/authStorage";
import {
  faRotateRight,
  faTimes,
  faExpand,
  faEye,
  faEyeSlash,
  faPen,
  faPlus,
  faTrash,
  faFloppyDisk,
} from "@fortawesome/free-solid-svg-icons";

import "./ImageResults.css";
import ResultsMap from "./ResultsMap";
import SolarReportMap from "./SolarReportMap";
import ThermalExtrema from "./ThermalExtrema";
import SolarAnalysisPanel, { SolarCandidateBoxes, SolarReviewSidebar, AnalysisStatus, candidateViewport } from "./SolarAnalysisPanel";
import useSolarAnalysis from "./useSolarAnalysis";
import AnomalySelection from "./AnomalySelection";
import SolarReportFields, { emptySolarReport, solarReportOptions } from "./SolarReportFields";
import RgbComparison, { thermalViewport } from "./RgbComparison";
import { isSolarThermalImage, matchingRgbImage } from "../../../utils/solarImages";
import { photoCoordinates, readMapFile } from "./mapLayers";
import { readImageMetadata } from "../../../utils/imageMetadata";
import { loadMapAsset } from "../../../utils/loadMapAsset";
import LoadingOverlay from "../../common/LoadingOverlay/LoadingOverlay";

/* ----------------------------- Constants ----------------------------- */

const PALETTE = {
  // Default / OK
  Aislador: { pill: "#e7f7ee", text: "#167c3c", bar: "#22c55e" },

  // Critical (red)
  AisladorRoto: { pill: "#ffecec", text: "#b42318", bar: "#ef4444" },
  DescargaParcial: { pill: "#ffecec", text: "#b42318", bar: "#ef4444" },
  FijacionDefectuosa: { pill: "#ffecec", text: "#b42318", bar: "#ef4444" },

  // Warning (orange)
  AisladorCorrosion: { pill: "#fff5e6", text: "#a16207", bar: "#f59e0b" },
  TorreFisurada: { pill: "#fff5e6", text: "#a16207", bar: "#f59e0b" },
  HerrajeCorrosion: { pill: "#fff5e6", text: "#a16207", bar: "#f59e0b" },
  ObjetoAjeno: { pill: "#fff5e6", text: "#a16207", bar: "#f59e0b" },
  TorreCorrosion: { pill: "#fff5e6", text: "#a16207", bar: "#f59e0b" },

  // Medium (yellow)
  AisladorContaminado: { pill: "#fff9db", text: "#854d0e", bar: "#ffee00" },
};

const DETECTION_CLASSES = [
  "Aislador",
  "AisladorRoto",
  "AisladorCorrosion",
  "AisladorContaminado",
  "AisladorPolimericoErosionado",
  "AtaduraDefectuosa",
  "ConductorDanado",
  "DescargaParcial",
  "FijacionDefectuosa",
  "HerrajeCorrosion",
  "HerrajeSuelto",
  "ObjetoAjeno",
  "PiezaFaltante",
  "TorreCorrosion",
  "TorreFisurada",
];

function DetectionClassTooltip({ active, payload, total }) {
  if (!active || !payload?.length) return null;

  const item = payload[0];
  const value = Number(item.value ?? 0);
  const percent = total > 0 ? Math.round((value / total) * 100) : 0;

  return (
    <div className="ir2-chart-tooltip">
      <div className="ir2-chart-tooltip-title">
        <span
          className="ir2-chart-tooltip-dot"
          style={{ background: item.payload.fill }}
        />
        {item.name}
      </div>
      <div className="ir2-chart-tooltip-value">
        {value} detection{value === 1 ? "" : "s"} · {percent}%
      </div>
    </div>
  );
}

const CLASS_TO_BOX_COLOR = (cls) => {
  switch (cls) {
    // 🔴 red
    case "AisladorRoto":
    case "DescargaParcial":
    case "ConductorDanado":
    case "PiezaFaltante":
    case "HerrajeSuelto":
    case "TorreFisurada":
    case "FijacionDefectuosa":
      return "#ff0000";

    // 🟠 orange
    case "AisladorCorrosion":
    case "HerrajeCorrosion":
    case "ObjetoAjeno":
    case "TorreCorrosion":
    case "AisladorPolimericoErosionado":
    case "AtaduraDefectuosa":
      return "#ff8f00";

    // 🟡 yellow
    case "AisladorContaminado":
      return "#ffee00";

    // ✅ default green
    default:
      return "#00ff13";
  }
};

const PIE_COLORS = [
  "#2563eb",
  "#16a34a",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#06b6d4",
  "#f97316",
  "#64748b",
];

/* ----------------------------- Helpers ----------------------------- */

const normalizeImageName = (name = "") =>
  String(name).replace(
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_/i,
    "",
  );

const getImgName = (img) => img?.imageName || img?.name || "";
const getImgId = (img) => img?.image_id || img?.imageId || img?.id || null;
const getResultImageId = (result) =>
  result?.image_id || result?.imageId || result?.image?.id || null;
const getImgUrl = (img) => {
  const raw = img?.originalUrl || img?.signedUrl || img?.imageUrl || img?.url || null;
  return window.__toAbsoluteUrl ? window.__toAbsoluteUrl(raw) : raw;
};
const normKey = (s) => normalizeImageName(s || "").toLowerCase();

function parseGcsFromPublicUrl(urlOrGs) {
  if (!urlOrGs) return null;

  const s = String(urlOrGs);

  // Proxy URL: /api/images/proxy?path=gs%3A%2F%2Fbucket%2Fpath
  if (s.includes('/api/images/proxy')) {
    try {
      const qs = s.includes('?') ? s.split('?')[1] : '';
      const gsPath = new URLSearchParams(qs).get('path');
      if (gsPath) return parseGcsFromPublicUrl(gsPath);
    } catch {}
  }

  let m = s.match(/^gs:\/\/([^/]+)\/(.+)$/);
  if (m) return { bucket: m[1], objectPath: m[2] };

  m = s.match(/^https:\/\/storage\.googleapis\.com\/([^/]+)\/(.+)$/);
  if (m) return { bucket: m[1], objectPath: m[2] };

  m = s.match(/^https:\/\/storage\.cloud\.google\.com\/([^/]+)\/(.+?)(\?.*)?$/);
  if (m) return { bucket: m[1], objectPath: m[2] };

  return null;
}

const getClassColor = (cls) => PALETTE?.[cls]?.bar ?? "#94a3b8";

/* ----------------------------- ImageRow ----------------------------- */

const reportImageKey = (imageOrName) => {
  const name = typeof imageOrName === "string" ? imageOrName : getImgName(imageOrName);
  return normKey(name);
};

const formatNotePreview = (notes = []) => {
  const text = notes
    .map((note) => {
      const author = note.authorName || note.authorEmail;
      const bodyText = String(note.note || "").trim();
      const body = note.label ? `${note.label}: ${bodyText}` : bodyText;
      return author ? `${body} - ${author}` : body;
    })
    .filter(Boolean)
    .join("\n\n");

  return { summary: text, fullText: text };
};

function ImageRow({
  row,
  isSelected,
  onClick,
  onToggleReportInclude,
  compactMode = false,
  showPoleColumn = true,
  onOpenNotes,
  onRetry,
}) {
  return (
    <div
      className={`ir2-tr ir2-row ${compactMode ? "ir2-tr-compact" : ""} ${isSelected ? "is-selected" : ""} ${row.isExcluded ? "is-excluded" : ""}`}
      onClick={() => { if (row.canOpen !== false) onClick(); }}
      aria-disabled={row.canOpen === false}
    >
      <div className="ir2-imagecell">
        <button
          type="button"
          className={`ir2-report-toggle ${row.isExcluded ? "is-excluded" : "is-included"}`}
          onClick={(event) => {
            event.stopPropagation();
            onToggleReportInclude(row.name);
          }}
          title={row.isExcluded ? "Include in report" : "Exclude from report"}
          aria-label={row.isExcluded ? "Include in report" : "Exclude from report"}
          aria-pressed={!row.isExcluded}
        >
          <FontAwesomeIcon icon={row.isExcluded ? faEyeSlash : faEye} />
        </button>
        <img className="ir2-thumb" src={row.thumbUrl || ""} alt={row.name} />
        <div className="ir2-imagecopy">
          <div className="ir2-filename">{row.name}</div>
          <div className="ir2-sub">{row.imageType || row.poleLineName}</div>
          {row.analysisStatus && <div className="solar-row-status">Analysis: <AnalysisStatus status={row.analysisStatus} onRetry={onRetry} /></div>}
        </div>
      </div>

      <div>
        {showPoleColumn ? (
          row.pole
        ) : (
          <span className={`ir2-badge ${row.isExcluded ? "is-no" : "is-yes"}`}>
            {row.isExcluded ? "Excluded" : "Included"}
          </span>
        )}
      </div>

      {compactMode ? (
        <button
          type="button"
          className={`ir2-note-preview ${row.notesSummary ? "" : "is-empty"}`}
          disabled={row.canOpen === false}
          data-full-note={row.notesFullText}
          onClick={(event) => {
            event.stopPropagation();
            onClick();
            onOpenNotes?.();
          }}
          title={row.notesFullText || "No notes for this image"}
        >
          <span className="ir2-note-preview-text">
            {row.notesSummary || "No notes"}
          </span>
          {row.notesFullText && (
            <span className="ir2-note-popup">
              {row.notesFullText}
            </span>
          )}
        </button>
      ) : (
        <>
          <div>
            <span className={`ir2-badge ${row.hasDet ? "is-yes" : "is-no"}`}>
              {row.hasDet ? "Yes" : "No"}
            </span>
            <span className="ir2-detcount">{row.totalDet}</span>
          </div>

          <div className="ir2-chips">
            {row.topClasses.length ? (
              row.topClasses.map(([cls, count]) => (
                <span key={cls} className="ir2-chip">
                  {cls}: <b>{count}</b>
                </span>
              ))
            ) : (
              <span className="ir2-muted">—</span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/* ----------------------------- Component ----------------------------- */

export default function ImageResults() {

  const location = useLocation();
  const [searchParams] = useSearchParams();
  const state = location.state ?? {};
  const { inspectionId: inspectionIdParam } = useParams();

  const {
    inspectionId: inspectionIdState,
    selectedImages: selectedImagesState,
    projectName,
    inspectionName,
    createdBy,
    createdAt,
    kmzFile,
    inspectionDomain,
    imageModality,
    thermalMode,
  } = state;

  const inspectionId = inspectionIdState || inspectionIdParam;
  const { isViewer } = useUser();
  const [inspectionDetails, setInspectionDetails] = useState(null);
  const effectiveImageModality = imageModality || inspectionDetails?.imageModality;
  const effectiveInspectionDomain = inspectionDomain || inspectionDetails?.inspectionDomain || "ELECTRIC_NETWORK";
  const isSolarInspection = effectiveInspectionDomain === "SOLAR_PANEL";
  const isThermalInspection = isSolarInspection || thermalMode || effectiveImageModality === "THERMAL";
  const hasAnalysisModel = !isThermalInspection;
  const isElectricNetworkInspection = effectiveInspectionDomain === "ELECTRIC_NETWORK";

  /* ----------------------------- State ----------------------------- */

  const [selectedImagesLocal, setSelectedImagesLocal] = useState(
    Array.isArray(selectedImagesState) ? selectedImagesState : [],
  );

  const [analysisData, setAnalysisData] = useState(null);
  const payload = analysisData ?? {};
  const results = useMemo(
    () => (Array.isArray(analysisData?.results) ? analysisData.results : []),
    [analysisData],
  );

  const [isLoadingResults, setIsLoadingResults] = useState(true);

  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const selectedImagesSafe = useMemo(
    () => isSolarInspection ? selectedImagesLocal.filter(isSolarThermalImage) : selectedImagesLocal,
    [selectedImagesLocal, isSolarInspection],
  );
  useEffect(() => { setSelectedImageIndex(0); }, [inspectionId, isSolarInspection]);
  const selectedImage = selectedImagesSafe[selectedImageIndex] ?? null;
  const isSelectedThermal = isThermalInspection && (!isSolarInspection || isSolarThermalImage(selectedImage));
  const [comparisonImages, setComparisonImages] = useState([]);
  const [comparisonError, setComparisonError] = useState("");
  const [showRgbComparison, setShowRgbComparison] = useState(false);
  const rgbImages = useMemo(() => {
    const byId = new Map();
    for (const image of [...comparisonImages, ...selectedImagesLocal]) {
      const id = getImgId(image);
      if (id && !isSolarThermalImage(image)) byId.set(String(id), { ...image, id: String(id), name: getImgName(image), url: getImgUrl(image) });
    }
    return [...byId.values()];
  }, [comparisonImages, selectedImagesLocal]);
  const thermalCount = useMemo(() => selectedImagesSafe.filter(isSolarThermalImage).length, [selectedImagesSafe]);
  useEffect(() => {
    setComparisonImages([]); setComparisonError("");
    if (!isSolarInspection) return;
    const controller = new AbortController();
    axios.get(`/api/inspections/${inspectionId}/images`, { signal: controller.signal, headers: { Authorization: `Bearer ${getAuthToken()}` } })
      .then(({ data }) => { if (!controller.signal.aborted) setComparisonImages(data.images || []); })
      .catch(() => { if (!controller.signal.aborted) setComparisonError("Could not load all RGB images. Reopen results to retry."); });
    return () => controller.abort();
  }, [inspectionId, isSolarInspection]);
  const automaticRgbId = matchingRgbImage(selectedImage, rgbImages)?.id || "";


  const [src, setSrc] = useState(null);

  // UI filters
  const [resultsView, setResultsView] = useState("list");
  const [mapLayers, setMapLayers] = useState([]);
  const reportMapCapture = useRef(null);
  const [mapLoadStatus, setMapLoadStatus] = useState("");
  const [recoveredGps, setRecoveredGps] = useState({});
  const [gpsStatus, setGpsStatus] = useState("");
  const savedKmz = kmzFile || inspectionDetails?.kmz_Url;
  useEffect(() => {
    const controller = new AbortController();
    setMapLoadStatus(savedKmz ? "Loading inspection KMZ…" : "");
    if (savedKmz) (async () => {
      try {
        const blob = await loadMapAsset(savedKmz, controller.signal);
        const file = new File([blob], "inspection.kmz");
        const layer = await readMapFile(file);
        if (!controller.signal.aborted) {
          setMapLayers(current => [{ ...layer, id: "inspection-kmz", name: "Inspection KMZ" }, ...current.filter(item => item.id !== "inspection-kmz")]);
          setMapLoadStatus("");
        }
      } catch (error) {
        if (!controller.signal.aborted) setMapLoadStatus("Could not load the saved KMZ. " + error.message);
      }
    })();
    return () => controller.abort();
  }, [inspectionId, savedKmz]);

  useEffect(() => {
    const controller = new AbortController();
    setRecoveredGps({});
    setGpsStatus("");
    const missing = selectedImagesSafe.filter(image => !photoCoordinates(image));
    if (!missing.length) return () => controller.abort();
    setGpsStatus("Reading GPS from original photos…");
    let next = 0;
    let failed = 0;
    async function worker() {
      while (next < missing.length && !controller.signal.aborted) {
        const image = missing[next++];
        try {
          const source = image instanceof Blob ? image : getImgUrl(image);
          if (!source) { failed++; continue; }
          const blob = await loadMapAsset(source, controller.signal);
          const metadata = await readImageMetadata(blob);
          const point = photoCoordinates(metadata);
          if (point && !controller.signal.aborted) setRecoveredGps(current => ({ ...current, [getImgId(image) || getImgName(image)]: point }));
          else failed++;
        } catch { failed++; }
      }
    }
    Promise.all([worker(), worker(), worker()]).then(() => {
      if (!controller.signal.aborted) setGpsStatus(failed ? `Could not recover GPS for ${failed} photos. Metadata may be missing or the download may have failed.` : "");
    });
    return () => controller.abort();
  }, [inspectionId, selectedImagesSafe]);

  const [listFilter, setListFilter] = useState("all"); // all | with | none
  const [selectedClass, setSelectedClass] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const [showNotesModal, setShowNotesModal] = useState(false);

  // PDF modal
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfOption, setPdfOption] = useState("current");
  const [solarReportValues, setSolarReportValues] = useState(emptySolarReport);
  useEffect(() => { setSolarReportValues(emptySolarReport); }, [inspectionId]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);

  // Export modal
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFilter, setExportFilter] = useState("all");
  const [isDownloadingZip, setIsDownloadingZip] = useState(false);
  const [exportError, setExportError] = useState(null);


  // Confidence filter (0–100 %)
  const [confidenceThreshold, setConfidenceThreshold] = useState(0);

  // Grouping
  const [groupMode, setGroupMode] = useState("flat"); // "flat" | "line"
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());

  // Zoom/pan (preview panel)
  const [zoomLevel, setZoomLevel] = useState(1);
  const [dragging, setDragging] = useState(false);
  const [imageOffset, setImageOffset] = useState({ x: 0, y: 0 });


  const imageContainerRef = useRef(null);
  const imageRef = useRef(null);

  // Fullscreen image modal
  const [showImageModal, setShowImageModal] = useState(false);
  useEffect(() => { setShowRgbComparison(false); }, [selectedImage, showImageModal]);
  const [showBBoxInModal, setShowBBoxInModal] = useState(true);
  const [modalZoom, setModalZoom] = useState(1);
  const [modalOffset, setModalOffset] = useState({ x: 0, y: 0 });
  const [modalDragging, setModalDragging] = useState(false);
  const [modalImageRect, setModalImageRect] = useState({
    left: 0,
    top: 0,
    width: 0,
    height: 0,
  });
  const modalContainerRef = useRef(null);
  const comparisonViewportRef = useRef(null);
  const modalImageRef = useRef(null);
  const modalDragRef = useRef({ active: false, lastX: 0, lastY: 0 });
  const detectionEditRef = useRef(null);
  const [isEditingDetections, setIsEditingDetections] = useState(false);
  const [editableDetections, setEditableDetections] = useState([]);
  const [selectedDetectionIndex, setSelectedDetectionIndex] = useState(null);
  const [isDrawingDetection, setIsDrawingDetection] = useState(false);
  const [isSavingDetections, setIsSavingDetections] = useState(false);
  const [detectionEditError, setDetectionEditError] = useState("");

  // For bounding-box scaling
  const [originalWidth, setOriginalWidth] = useState(0);
  const [originalHeight, setOriginalHeight] = useState(0);
  const [imageWidth, setImageWidth] = useState(0);
  const [imageHeight, setImageHeight] = useState(0);
  const [imageLeft, setImageLeft] = useState(0);
  const [imageTop, setImageTop] = useState(0);

  const selName = getImgName(selectedImage);
  const selectedImageReportKey = reportImageKey(selName);

  /* ----------------------------- Derived data ----------------------------- */

  const [excludedReportImageKeys, setExcludedReportImageKeys] = useState(() => new Set());
  const [notesByImageId, setNotesByImageId] = useState({});
  const [noteDraft, setNoteDraft] = useState("");
  const [noteLabelDraft, setNoteLabelDraft] = useState("");
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [notesError, setNotesError] = useState("");
  const [isAddingPointNote, setIsAddingPointNote] = useState(false);
  const [addingManualAnomaly,setAddingManualAnomaly] = useState(false);
  const [pendingNoteAnnotation, setPendingNoteAnnotation] = useState(null);
  const [showThermalExtrema, setShowThermalExtrema] = useState(false);
  const [selectingThermalArea, setSelectingThermalArea] = useState(false);
  const [showAnomalies, setShowAnomalies] = useState(true);
  const [selectingAnomalies, setSelectingAnomalies] = useState(false);
  const [selectedAnomalyIds, setSelectedAnomalyIds] = useState(() => new Set());
  const [thermalProbe, setThermalProbe] = useState(null);
  const thermalProbeRef = useRef({ lastAt: 0 });

  // Index results by normalized name
  const resultByName = useMemo(() => {
    const m = new Map();
    for (const r of results || []) m.set(normKey(r.image_name), r);
    return m;
  }, [results]);

  const selectedResult = useMemo(() => {
    if (!selName) return null;
    return resultByName.get(normKey(selName)) ?? null;
  }, [resultByName, selName]);
  const selectedImageId = getResultImageId(selectedResult) || getImgId(selectedImage);
  const solar = useSolarAnalysis({ inspectionId, images: selectedImagesLocal, enabled: isSolarInspection, selectedImageId, viewerOpen: showImageModal });
  const { canOpen: canOpenImage, tasks: solarTasks, loaded: solarLoaded, starting: solarStarting } = solar;
  const canViewSelected = canOpenImage(selectedImageId);
  const solarCandidates = solar.detail?.imageId === selectedImageId ? solar.detail.candidates : [];
  useEffect(() => {
    setSelectingAnomalies(false); setSelectedAnomalyIds(new Set());
  }, [selectedImageId, showImageModal]);
  useEffect(() => {
    const active = new Set((solar.detail?.candidates || []).filter(candidate => candidate.reviewStatus !== 'DISMISSED').map(candidate => candidate.id));
    setSelectedAnomalyIds(current => new Set([...current].filter(id => active.has(id))));
  }, [solar.detail]);
  useEffect(() => { if (!canViewSelected) { setShowImageModal(false); setShowNotesModal(false); } }, [canViewSelected]);

  useEffect(() => { setSelectingThermalArea(false); }, [selectedImageId, showImageModal]);


  const imageByName = useMemo(() => {
    const m = new Map();
    for (const img of selectedImagesSafe || []) {
      m.set(normKey(getImgName(img)), img);
    }
    return m;
  }, [selectedImagesSafe]);

  const selectedImageNotes = selectedImageId ? notesByImageId[selectedImageId] || [] : [];

  const totalImages = isSolarInspection ? selectedImagesSafe.length : payload.totalSelected ?? selectedImagesSafe.length ?? 0;
  const analyzedImagesCount = isThermalInspection
    ? (isSolarInspection ? thermalCount : selectedImagesSafe.length)
    : payload.processed ?? results.length ?? 0;
  const includedReportCount = Math.max(0, selectedImagesSafe.length - excludedReportImageKeys.size);
  const excludedReportCount = excludedReportImageKeys.size;

  const toggleReportInclude = useCallback((imageName) => {
    const key = reportImageKey(imageName);
    if (!key) return;

    setExcludedReportImageKeys((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  // Helper: filter detections by current confidence threshold
  const minConf = confidenceThreshold / 100;
  const filteredDets = useCallback(
    (r) => (Array.isArray(r?.detections) ? r.detections.filter((d) => (d.confidence ?? 1) >= minConf) : []),
    [minConf],
  );

  const notesForReportImage = useCallback(
    (result, notesIndex = notesByImageId) => {
      const image = imageByName.get(normKey(result?.image_name));
      const imageId = getResultImageId(result) || getImgId(image);
      if (!imageId) return [];
      return (notesIndex[imageId] || []).map((note) => ({
        note: note.annotationType === "POINT"
          ? `${note.label ? `${note.label}: ` : ""}${note.note} (Point: ${Math.round((Number(note.xNorm) || 0) * 100)}%, ${Math.round((Number(note.yNorm) || 0) * 100)}%)`
          : note.note,
        authorName: note.authorName,
        authorEmail: note.authorEmail,
      }));
    },
    [imageByName, notesByImageId],
  );

  const loadImageNotes = useCallback(async (imageId, { force = false } = {}) => {
    if (!inspectionId || !imageId) return [];
    if (!force && notesByImageId[imageId]) return notesByImageId[imageId];

    const token = getAuthToken();
    const res = await axios.get(
      `/api/inspections/${inspectionId}/images/${imageId}/notes`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    const notes = Array.isArray(res.data) ? res.data : [];
    setNotesByImageId((current) => ({ ...current, [imageId]: notes }));
    return notes;
  }, [inspectionId, notesByImageId]);

  const ensureReportNotesLoaded = useCallback(async ({ results, pdfOption, selectedImageName }) => {
    const opt = (pdfOption || "").toLowerCase();
    const sourceResults = isThermalInspection
      ? selectedImagesSafe.map((image) => ({
          image_id: getImgId(image),
          image_name: getImgName(image),
          detections: [],
        }))
      : results || [];
    const reportableResults = sourceResults.filter(
      (result) => !excludedReportImageKeys.has(reportImageKey(result.image_name)),
    );

    const items = opt === "all"
      ? reportableResults
      : opt === "detections" && !isThermalInspection
        ? reportableResults.filter((result) => filteredDets(result).length > 0)
        : reportableResults.filter((result) => normKey(result.image_name) === normKey(selectedImageName));

    const ids = [...new Set(items
      .map((result) => getResultImageId(result) || getImgId(imageByName.get(normKey(result?.image_name))))
      .filter(Boolean))];

    if (ids.length === 0) return notesByImageId;

    const loaded = await Promise.all(ids.map((id) => loadImageNotes(id)));
    const next = { ...notesByImageId };
    ids.forEach((id, index) => {
      next[id] = loaded[index] || [];
    });
    return next;
  }, [excludedReportImageKeys, filteredDets, imageByName, isThermalInspection, loadImageNotes, notesByImageId, selectedImagesSafe]);

  const submitNote = useCallback(async () => {
    const trimmed = noteDraft.trim();
    if (!trimmed || !selectedImageId) return;

    const isPointNote = pendingNoteAnnotation?.annotationType === "POINT";
    const hasPointCoordinates = Number.isFinite(pendingNoteAnnotation?.xNorm)
      && Number.isFinite(pendingNoteAnnotation?.yNorm);
    if (isPointNote && !hasPointCoordinates) {
      setNotesError("Select a valid point on the image before saving this note.");
      return;
    }

    setIsSavingNote(true);
    setNotesError("");
    try {
      const token = getAuthToken();
      const annotationPayload = isPointNote
        ? {
            annotationType: "POINT",
            xNorm: pendingNoteAnnotation.xNorm,
            yNorm: pendingNoteAnnotation.yNorm,
            temperatureC: pendingNoteAnnotation.temperatureC,
            label: noteLabelDraft.trim() || null,
          }
        : { annotationType: "GENERAL" };
      const res = await axios.post(
        `/api/inspections/${inspectionId}/images/${selectedImageId}/notes`,
        { note: trimmed, ...annotationPayload },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setNotesByImageId((current) => ({
        ...current,
        [selectedImageId]: [...(current[selectedImageId] || []), res.data],
      }));
      setNoteDraft("");
      setNoteLabelDraft("");
      setPendingNoteAnnotation(null);
    } catch (e) {
      setNotesError(e.response?.data?.message || e.response?.data?.error || "Could not save the note.");
    } finally {
      setIsSavingNote(false);
    }
  }, [inspectionId, noteDraft, noteLabelDraft, pendingNoteAnnotation, selectedImageId]);

  const totalDetections = useMemo(() => {
    return (results || []).reduce((acc, r) => acc + filteredDets(r).length, 0);
  }, [results, filteredDets]);

  const polesWithImages = useMemo(() => {
    const s = new Set();
    for (const r of results || []) {
      if (r?.posteId) s.add(r.posteId);
      else if (r?.posteNombre) s.add(r.posteNombre);
    }
    return s.size;
  }, [results]);

  const detectionsByClass = useMemo(() => {
    const agg = {};
    for (const r of results || []) {
      for (const d of filteredDets(r)) {
        const cls = d.cls || "Unknown";
        agg[cls] = (agg[cls] || 0) + 1;
      }
    }
    return Object.entries(agg)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [results, filteredDets]);

  const tableRows = useMemo(() => {
    return (selectedImagesSafe || []).map((img, idx) => {
      const name = getImgName(img);
      const imageId = getImgId(img);
      const r = resultByName.get(normKey(name)) || null;
      const notePreview = formatNotePreview(notesByImageId[imageId] || []);

      const dets = filteredDets(r);
      const totalDet = dets.length;
      const hasDet = totalDet > 0;

      const classCounts = dets.reduce((acc, d) => {
        const cls = d.cls || "Unknown";
        acc[cls] = (acc[cls] || 0) + 1;
        return acc;
      }, {});
      const topClasses = Object.entries(classCounts)
        .sort((a, b) => (b[1] || 0) - (a[1] || 0))
        .slice(0, 3);

      return {
        idx,
        imageId,
        name,
        isExcluded: excludedReportImageKeys.has(reportImageKey(name)),
        thumbUrl: getImgUrl(img),
        canOpen: canOpenImage(imageId),
        imageType: isSolarInspection ? (isSolarThermalImage(img) ? "Thermal" : "RGB") : null,
        analysisStatus: isSolarInspection && isSolarThermalImage(img) ? (!solarLoaded || solarStarting ? "CHECKING" : solarTasks.find(task => String(task.image_id) === String(imageId))?.status || "NOT ANALYZED") : null,
        analysisTaskId: isSolarInspection && !isSolarThermalImage(img) ? null : solarTasks.find(task => String(task.image_id) === String(imageId))?.id,
        coordinates: photoCoordinates(img, r) || recoveredGps[imageId || name] || null,
        pole: r?.posteNombre || "—",
        poleLineName: r?.poleLineName || "—",
        hasDet,
        totalDet,
        topClasses,
        notesSummary: notePreview.summary,
        notesFullText: notePreview.fullText,
      };
    });
  }, [selectedImagesSafe, resultByName, filteredDets, excludedReportImageKeys, notesByImageId, recoveredGps, canOpenImage, solarLoaded, solarStarting, solarTasks, isSolarInspection]);

  const reportScopedRows = tableRows;

  const filteredRows = useMemo(() => {
    if (!hasAnalysisModel) return reportScopedRows;
    if (listFilter === "with") return reportScopedRows.filter((r) => r.hasDet);
    if (listFilter === "none") return reportScopedRows.filter((r) => !r.hasDet);
    return reportScopedRows;
  }, [reportScopedRows, listFilter, hasAnalysisModel]);

  const classFilteredRows = useMemo(() => {
    if (!hasAnalysisModel || !selectedClass) return filteredRows;

    return filteredRows.filter((row) => {
      const r = resultByName.get(normKey(row.name));
      return filteredDets(r).some((d) => d.cls === selectedClass);
    });
  }, [filteredRows, selectedClass, resultByName, filteredDets, hasAnalysisModel]);

  // Grouped view: classFilteredRows → Map<lineName, {rows, totalDet, totalImages}>
  const groupedRows = useMemo(() => {
    const map = new Map();
    for (const row of classFilteredRows) {
      const key = row.poleLineName && row.poleLineName !== "—"
        ? row.poleLineName
        : "Unassigned line";
      if (!map.has(key)) map.set(key, { lineName: key, rows: [], totalDet: 0 });
      const g = map.get(key);
      g.rows.push(row);
      g.totalDet += row.totalDet;
    }
    return Array.from(map.values()).sort((a, b) => a.lineName.localeCompare(b.lineName));
  }, [classFilteredRows]);

  const toggleGroup = (lineName) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      next.has(lineName) ? next.delete(lineName) : next.add(lineName);
      return next;
    });
  };

  const totalPages = Math.max(
    1,
    Math.ceil(classFilteredRows.length / pageSize),
  );

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return classFilteredRows.slice(start, start + pageSize);
  }, [classFilteredRows, page]);

  useEffect(() => {
    const visibleRows = groupMode === "flat"
      ? pagedRows
      : classFilteredRows.slice(0, 50);
    const ids = [...new Set(visibleRows.map((row) => row.imageId).filter(Boolean))];

    ids.forEach((imageId) => {
      if (notesByImageId[imageId]) return;
      loadImageNotes(imageId).catch((e) => {
        console.error("Error loading row notes", e);
      });
    });
  }, [classFilteredRows, groupMode, loadImageNotes, notesByImageId, pagedRows]);

  useEffect(() => {
    // clamp por si cambió totalPages
    setPage((p) => Math.min(Math.max(1, p), totalPages));
  }, [totalPages]);

  const appliedImageLink = useRef(null);
  useEffect(() => {
    const targetImageId = searchParams.get("imageId");
    const linkKey = `${inspectionId}:${targetImageId}`;
    if (!targetImageId) { appliedImageLink.current = null; return; }
    if (appliedImageLink.current === linkKey) return;
    if (!targetImageId || selectedImagesSafe.length === 0) return;

    const targetIndex = selectedImagesSafe.findIndex((img) => {
      if (String(getImgId(img)) === String(targetImageId)) return true;
      const result = resultByName.get(normKey(getImgName(img)));
      return String(getResultImageId(result)) === String(targetImageId);
    });

    if (targetIndex >= 0) {
      appliedImageLink.current = linkKey;
      setSelectedImageIndex(targetIndex);
      const rowPosition = classFilteredRows.findIndex((row) => row.idx === targetIndex);
      if (rowPosition >= 0) {
        setPage(Math.floor(rowPosition / pageSize) + 1);
      }
    }
  }, [searchParams, selectedImagesSafe, resultByName, classFilteredRows, inspectionId]);

  // Bounding boxes for selected image (filtered by confidence threshold)
  const boundingBoxes = useMemo(() => {
    if (!selectedResult) return [];
    return filteredDets(selectedResult);
  }, [selectedResult, filteredDets]);

  const modalDetections = isEditingDetections
    ? editableDetections
    : boundingBoxes;

  const availableDetectionClasses = useMemo(
    () =>
      Array.from(
        new Set([
          ...DETECTION_CLASSES,
          ...editableDetections.map((detection) => detection.cls).filter(Boolean),
        ]),
      ).sort((a, b) => a.localeCompare(b)),
    [editableDetections],
  );

  const coordsToShow = useMemo(() => {
    const posteLat = selectedResult?.posteLat;
    const posteLon = selectedResult?.posteLong;
    if (posteLat != null && posteLon != null)
      return { lat: posteLat, lon: posteLon };

    const point = photoCoordinates(selectedImage, selectedResult) || recoveredGps[getImgId(selectedImage) || getImgName(selectedImage)];
    if (point) return { lat: point[0], lon: point[1] };
    const imgLat = selectedResult?.imageLat;
    const imgLon = selectedResult?.imageLon;
    if (imgLat != null && imgLon != null) return { lat: imgLat, lon: imgLon };

    return null;
  }, [selectedResult, selectedImage, recoveredGps]);

  const readyForBoxes =
    originalWidth > 0 &&
    originalHeight > 0 &&
    imageWidth > 0 &&
    imageHeight > 0;

  const measurePreviewImage = useCallback(() => {
    const el = imageRef.current;
    if (!el || !el.naturalWidth || !el.naturalHeight) return;

    const update = () => {
      const boxWidth = el.clientWidth || el.offsetWidth;
      const boxHeight = el.clientHeight || el.offsetHeight;
      const naturalAspect = el.naturalWidth / el.naturalHeight;
      const boxAspect = boxWidth / boxHeight;
      let renderedWidth = boxWidth;
      let renderedHeight = boxHeight;
      let renderedLeft = 0;
      let renderedTop = 0;

      if (boxAspect > naturalAspect) {
        renderedHeight = boxHeight;
        renderedWidth = boxHeight * naturalAspect;
        renderedLeft = (boxWidth - renderedWidth) / 2;
      } else {
        renderedWidth = boxWidth;
        renderedHeight = boxWidth / naturalAspect;
        renderedTop = (boxHeight - renderedHeight) / 2;
      }

      setOriginalWidth(el.naturalWidth);
      setOriginalHeight(el.naturalHeight);
      setImageWidth(renderedWidth);
      setImageHeight(renderedHeight);
      setImageLeft(renderedLeft);
      setImageTop(renderedTop);
    };

    update();
    requestAnimationFrame(update);
  }, []);

  /* ----------------------------- Effects ----------------------------- */

  useEffect(() => { setMapLayers([]); }, [inspectionId]);

  // Reset pagination when filters change
  useEffect(() => {
    setPage(1);
  }, [listFilter, selectedClass, selectedImagesSafe.length]);

  useEffect(() => {
    if (!isElectricNetworkInspection && groupMode !== "flat") {
      setGroupMode("flat");
    }
  }, [groupMode, isElectricNetworkInspection]);

  // Reset zoom/pan when selection changes
  useEffect(() => {
    setZoomLevel(1);
    setImageOffset({ x: 0, y: 0 });
    setDragging(false);
    setDragging(false);
  }, [selectedImageIndex]);

  useEffect(() => {
    setNoteDraft("");
    setNoteLabelDraft("");
    setNotesError("");
    setPendingNoteAnnotation(null);
    setIsAddingPointNote(false); setAddingManualAnomaly(false);
    setThermalProbe(null);
    if (!selectedImageId) return;
    loadImageNotes(selectedImageId).catch((e) => {
      console.error("Error loading image notes", e);
      setNotesError("Could not load notes for this image.");
    });
  }, [selectedImageId, loadImageNotes]);

  // Resolve src
  // Resolve src
  useEffect(() => {
    if (!selectedImage) {
      setSrc(null);
      return;
    }

    if (selectedImage instanceof Blob) {
      const u = URL.createObjectURL(selectedImage);
      setSrc(u);
      return () => URL.revokeObjectURL(u);
    }

    setSrc(getImgUrl(selectedImage));
  }, [selectedImage]);

  // Load inspection metadata if coming from a direct link/reload.
  useEffect(() => {
    if (!inspectionId) return;


    let cancelled = false;
    (async () => {
      try {
        const token = getAuthToken();
        const res = await axios.get(`/api/inspections/${inspectionId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!cancelled) setInspectionDetails(res.data || null);
      } catch (e) {
        console.error("Error loading inspection details", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [inspectionId, imageModality, inspectionDomain, inspectionName, projectName]);

  // Load images if coming from reload (no state)
  useEffect(() => {
    if (!inspectionId) return;
    if (selectedImagesLocal.length > 0) return;

    let cancelled = false;
    (async () => {
      try {
        const token = getAuthToken();
        const res = await axios.get(
          `/api/inspections/${inspectionId}/images`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (cancelled) return;

        const imgs = Array.isArray(res.data?.images) ? res.data.images : [];
        const normalized = imgs.map((img) => ({
          ...img,
          originalUrl: img.signedUrl || img.imageUrl || img.url || null,
          imageName: img.imageName || img.name || "",
          name: img.imageName || img.name || "",
        }));

        setSelectedImagesLocal(normalized);
      } catch (e) {
        console.error("Error loading inspection images", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [inspectionId, selectedImagesLocal.length]);

  // Poll results
  useEffect(() => {
    if (!inspectionId) return;

    if (isThermalInspection) {
      setAnalysisData({
        results: [],
        processed: selectedImagesSafe.length,
        totalSelected: selectedImagesSafe.length,
        done: true,
      });
      setIsLoadingResults(false);
      return;
    }

    let cancelled = false;
    let timer = null;

    const token = getAuthToken();

    const fetchResults = async () => {
      try {
        const res = await axios.get(
          `/api/${inspectionId}/predictions`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (cancelled) return;

        const data = res.data?.body ?? res.data ?? {};
        const resultsArr = Array.isArray(data.results)
          ? data.results
          : Array.isArray(data)
            ? data
            : [];

        const totalFromBack = Number.isFinite(data.totalSelected)
          ? data.totalSelected
          : null;
        const processedFromBack = Number.isFinite(data.processed)
          ? data.processed
          : resultsArr.length;

        const totalFallback =
          selectedImagesSafe.length || resultsArr.length || 0;
        const total = totalFromBack ?? totalFallback;

        const doneFromBack = typeof data.done === "boolean" ? data.done : null;
        const doneFallback = total > 0 && processedFromBack >= total;
        const done = doneFromBack ?? doneFallback;

        setAnalysisData({
          results: resultsArr,
          processed: processedFromBack,
          totalSelected: total,
          done,
        });

        // Only keep showing "loading" if we have zero results yet.
        // If there are partial results, let the user interact — stop blocking.
        const hasResults = resultsArr.length > 0;
        setIsLoadingResults(!done && !hasResults);

        // Keep polling only while actively analyzing (no results yet).
        // Once we have results the user can act; they can refresh manually if needed.
        if (!done && !hasResults) timer = setTimeout(fetchResults, 3000);
      } catch {
        if (!cancelled) timer = setTimeout(fetchResults, 4000);
      }
    };

    fetchResults();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [inspectionId, isThermalInspection, selectedImagesSafe.length]);

  // Measure image sizes for box scaling — re-runs on resize too
  useEffect(() => {
    const el = imageRef.current;
    setOriginalWidth(0);
    setOriginalHeight(0);
    setImageWidth(0);
    setImageHeight(0);
    setImageLeft(0);
    setImageTop(0);

    if (!el || !src) return;

    // Measure immediately if already loaded, otherwise wait for load
    if (el.complete && el.naturalWidth > 0) {
      measurePreviewImage();
    } else {
      el.addEventListener("load", measurePreviewImage);
    }

    // Re-measure whenever the element's rendered size changes (window resize, panel resize…)
    const ro = new ResizeObserver(measurePreviewImage);
    ro.observe(el);

    return () => {
      el.removeEventListener("load", measurePreviewImage);
      ro.disconnect();
    };
  }, [src, measurePreviewImage, canViewSelected]);

  /* ----------------------------- Zoom & Pan ----------------------------- */

  const clampOffset = useCallback(
    (x, y) => {
      const container = imageContainerRef.current;
      const img = imageRef.current;
      if (!container || !img) return { x, y };

      const cw = container.clientWidth;
      const ch = container.clientHeight;

      const scaledW = img.offsetWidth * zoomLevel;
      const scaledH = img.offsetHeight * zoomLevel;

      const minX = Math.min(0, cw - scaledW);
      const maxX = 0;
      const minY = Math.min(0, ch - scaledH);
      const maxY = 0;

      return {
        x: Math.min(maxX, Math.max(minX, x)),
        y: Math.min(maxY, Math.max(minY, y)),
      };
    },
    [zoomLevel],
  );

  const dragRef = useRef({ active: false, lastX: 0, lastY: 0 });

  const handleZoom = useCallback(
    (event) => {
      event.preventDefault();
      setZoomLevel((prev) => {
        const next = Math.min(Math.max(prev + event.deltaY * -0.001, 1), 8);
        requestAnimationFrame(() =>
          setImageOffset((o) => clampOffset(o.x, o.y)),
        );
        return next;
      });
    },
    [clampOffset],
  );

  const handleMouseDown = useCallback(
    (e) => {
      if (zoomLevel <= 1) return;
      dragRef.current = { active: true, lastX: e.clientX, lastY: e.clientY };
      setDragging(true);
    },
    [zoomLevel],
  );

  const handleMouseMove = useCallback(
    (e) => {
      if (!dragRef.current.active) return;

      const dx = e.clientX - dragRef.current.lastX;
      const dy = e.clientY - dragRef.current.lastY;

      dragRef.current.lastX = e.clientX;
      dragRef.current.lastY = e.clientY;

      const adjX = dx / zoomLevel;
      const adjY = dy / zoomLevel;

      setImageOffset((prev) => clampOffset(prev.x + adjX, prev.y + adjY));
    },
    [zoomLevel, clampOffset],
  );

  const handleMouseUp = useCallback(() => {
    dragRef.current.active = false;
    setDragging(false);
  }, []);

  const resetView = useCallback(() => {
    const container = imageContainerRef.current;
    const img = imageRef.current;

    setZoomLevel(1);

    if (!container || !img) {
      setImageOffset({ x: 0, y: 0 });
      return;
    }

    const cw = container.clientWidth;
    const ch = container.clientHeight;
    const iw = img.offsetWidth;
    const ih = img.offsetHeight;

    setImageOffset({
      x: Math.round((cw - iw) / 2),
      y: Math.round((ch - ih) / 2),
    });
  }, []);

  const isCentered =
    zoomLevel === 1 &&
    Math.abs(imageOffset.x) < 1 &&
    Math.abs(imageOffset.y) < 1;

  /* ----------------------------- Modal zoom & pan ----------------------------- */

  const openImageModal = useCallback((index) => {
    const target = typeof index === "number" ? index : selectedImageIndex;
    if (!canOpenImage(getImgId(selectedImagesSafe[target]))) return;
    setSelectedImageIndex(target);
    setModalZoom(1);
    setModalOffset({ x: 0, y: 0 });
    setModalDragging(false);
    setIsEditingDetections(false);
    setEditableDetections([]);
    setSelectedDetectionIndex(null);
    setIsDrawingDetection(false);
    setDetectionEditError("");
    setShowImageModal(true);
  }, [selectedImageIndex, selectedImagesSafe, canOpenImage]);

  const closeImageModal = useCallback(() => {
    detectionEditRef.current = null;
    setIsEditingDetections(false);
    setIsDrawingDetection(false);
    setSelectedDetectionIndex(null);
    setShowImageModal(false);
  }, []);

  const cloneDetections = useCallback(
    (detections) =>
      detections.map((detection) => ({
        ...detection,
        confidence: detection.confidence ?? 1,
        boundingBox: { ...detection.boundingBox },
      })),
    [],
  );

  const startDetectionEditing = useCallback(() => {
    setEditableDetections(cloneDetections(boundingBoxes));
    setSelectedDetectionIndex(boundingBoxes.length > 0 ? 0 : null);
    setIsDrawingDetection(false);
    setDetectionEditError("");
    setShowBBoxInModal(true);
    setIsEditingDetections(true);
  }, [boundingBoxes, cloneDetections]);

  const cancelDetectionEditing = useCallback(() => {
    detectionEditRef.current = null;
    setEditableDetections([]);
    setSelectedDetectionIndex(null);
    setIsDrawingDetection(false);
    setDetectionEditError("");
    setIsEditingDetections(false);
  }, []);

  const clientToOriginalPoint = useCallback(
    (clientX, clientY) => {
      const container = modalContainerRef.current;
      if (!container || modalImageRect.width <= 0 || modalImageRect.height <= 0
          || originalWidth <= 0 || originalHeight <= 0) {
        return null;
      }

      const rect = container.getBoundingClientRect();
      const contentX = (clientX - rect.left - modalOffset.x) / modalZoom;
      const contentY = (clientY - rect.top - modalOffset.y) / modalZoom;
      const renderedX = contentX - modalImageRect.left;
      const renderedY = contentY - modalImageRect.top;

      return {
        x: Math.max(0, Math.min(originalWidth,
          renderedX * (originalWidth / modalImageRect.width))),
        y: Math.max(0, Math.min(originalHeight,
          renderedY * (originalHeight / modalImageRect.height))),
      };
    },
    [
      modalImageRect,
      modalOffset,
      modalZoom,
      originalWidth,
      originalHeight,
    ],
  );

  const startPointNote = useCallback(() => {
    setSelectingAnomalies(false);
    setSelectingThermalArea(false);
    setIsAddingPointNote(true); setAddingManualAnomaly(false);
    setIsDrawingDetection(false);
    setSelectedDetectionIndex(null);
    setDetectionEditError("");
  }, []);

  const beginPointNote = useCallback((event) => {
    if (!isSelectedThermal || !isAddingPointNote || !selectedImageId) return false;
    const point = clientToOriginalPoint(event.clientX, event.clientY);
    if (!point || originalWidth <= 0 || originalHeight <= 0) return false;

    event.preventDefault();
    event.stopPropagation();
    setPendingNoteAnnotation({
      annotationType: "POINT",
      xNorm: point.x / originalWidth,
      yNorm: point.y / originalHeight,
      widthNorm: null,
      heightNorm: null,
      temperatureC: Number.isFinite(thermalProbe?.temperatureC)
        ? thermalProbe.temperatureC
        : null,
    });
    setNoteDraft("");
    setNoteLabelDraft("");
    setNotesError("");
    setShowNotesModal(true);
    setIsAddingPointNote(false); setAddingManualAnomaly(false);
    return true;
  }, [
    clientToOriginalPoint,
    isAddingPointNote,
    isSelectedThermal,
    originalHeight,
    originalWidth,
    selectedImageId,
    thermalProbe,
  ]);

  const updateThermalProbe = useCallback((event) => {
    if (!isSelectedThermal || !inspectionId || !selectedImageId) {
      return;
    }
    const point = clientToOriginalPoint(event.clientX, event.clientY);
    if (!point || originalWidth <= 0 || originalHeight <= 0) return;

    const xNorm = Math.max(0, Math.min(1, point.x / originalWidth));
    const yNorm = Math.max(0, Math.min(1, point.y / originalHeight));
    const stageRect = modalContainerRef.current?.getBoundingClientRect();
    const screenX = stageRect ? event.clientX - stageRect.left : 0;
    const screenY = stageRect ? event.clientY - stageRect.top : 0;

    const now = Date.now();
    setThermalProbe((current) => ({
      ...(current || {}),
      x: screenX,
      y: screenY,
      xNorm,
      yNorm,
      loading: !Number.isFinite(current?.temperatureC),
    }));

    if (now - thermalProbeRef.current.lastAt < 180) return;
    thermalProbeRef.current.lastAt = now;

    axios.get(
      `/api/inspections/${inspectionId}/images/${selectedImageId}/thermal/temperature`,
      {
        params: { xNorm, yNorm },
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      },
    ).then((res) => {
      setThermalProbe((current) => ({
        ...(current || {}),
        ...res.data,
        x: current?.x ?? screenX,
        y: current?.y ?? screenY,
        xNorm: current?.xNorm ?? xNorm,
        yNorm: current?.yNorm ?? yNorm,
        loading: false,
      }));
    }).catch((error) => {
      setThermalProbe((current) => ({
        ...(current || {}),
        x: current?.x ?? screenX,
        y: current?.y ?? screenY,
        xNorm: current?.xNorm ?? xNorm,
        yNorm: current?.yNorm ?? yNorm,
        loading: false,
        status: "ERROR",
        message: error.response?.data?.message
          || error.response?.data?.error
          || error.message
          || "Temperature unavailable",
      }));
    });
  }, [
    clientToOriginalPoint,
    inspectionId,
    isSelectedThermal,
    originalHeight,
    originalWidth,
    selectedImageId,
  ]);

  const beginDetectionTransform = useCallback(
    (event, index, mode, handle = "") => {
      if (!isEditingDetections) return;
      event.preventDefault();
      event.stopPropagation();

      const point = clientToOriginalPoint(event.clientX, event.clientY);
      const detection = editableDetections[index];
      if (!point || !detection?.boundingBox) return;

      setSelectedDetectionIndex(index);
      setIsDrawingDetection(false);
      detectionEditRef.current = {
        mode,
        handle,
        index,
        start: point,
        box: { ...detection.boundingBox },
      };
    },
    [isEditingDetections, clientToOriginalPoint, editableDetections],
  );

  const beginDrawingDetection = useCallback(
    (event) => {
      if (!isEditingDetections || !isDrawingDetection) return false;

      const point = clientToOriginalPoint(event.clientX, event.clientY);
      if (!point) return false;

      event.preventDefault();
      event.stopPropagation();
      const nextIndex = editableDetections.length;
      const className = availableDetectionClasses[0] || "Aislador";
      const detection = {
        cls: className,
        confidence: 1,
        boundingBox: {
          x1: Math.round(point.x),
          y1: Math.round(point.y),
          x2: Math.round(point.x),
          y2: Math.round(point.y),
        },
      };

      setEditableDetections((current) => [...current, detection]);
      setSelectedDetectionIndex(nextIndex);
      detectionEditRef.current = {
        mode: "draw",
        index: nextIndex,
        start: point,
        box: { ...detection.boundingBox },
      };
      return true;
    },
    [
      isEditingDetections,
      isDrawingDetection,
      clientToOriginalPoint,
      editableDetections.length,
      availableDetectionClasses,
    ],
  );

  const updateDetectionTransform = useCallback(
    (event) => {
      const edit = detectionEditRef.current;
      if (!edit) return false;

      const point = clientToOriginalPoint(event.clientX, event.clientY);
      if (!point) return true;

      event.preventDefault();
      const dx = point.x - edit.start.x;
      const dy = point.y - edit.start.y;
      let nextBox = { ...edit.box };

      if (edit.mode === "draw") {
        nextBox = {
          x1: Math.min(edit.start.x, point.x),
          y1: Math.min(edit.start.y, point.y),
          x2: Math.max(edit.start.x, point.x),
          y2: Math.max(edit.start.y, point.y),
        };
      } else if (edit.mode === "move") {
        const width = edit.box.x2 - edit.box.x1;
        const height = edit.box.y2 - edit.box.y1;
        const x1 = Math.max(0, Math.min(originalWidth - width, edit.box.x1 + dx));
        const y1 = Math.max(0, Math.min(originalHeight - height, edit.box.y1 + dy));
        nextBox = { x1, y1, x2: x1 + width, y2: y1 + height };
      } else if (edit.mode === "resize") {
        if (edit.handle.includes("w")) nextBox.x1 = point.x;
        if (edit.handle.includes("e")) nextBox.x2 = point.x;
        if (edit.handle.includes("n")) nextBox.y1 = point.y;
        if (edit.handle.includes("s")) nextBox.y2 = point.y;
        const rawX1 = nextBox.x1;
        const rawY1 = nextBox.y1;
        const rawX2 = nextBox.x2;
        const rawY2 = nextBox.y2;
        nextBox = {
          x1: Math.min(rawX1, rawX2),
          y1: Math.min(rawY1, rawY2),
          x2: Math.max(rawX1, rawX2),
          y2: Math.max(rawY1, rawY2),
        };
      }

      setEditableDetections((current) =>
        current.map((detection, index) =>
          index === edit.index
            ? {
                ...detection,
                boundingBox: Object.fromEntries(
                  Object.entries(nextBox).map(([key, value]) => [
                    key,
                    Math.round(value),
                  ]),
                ),
              }
            : detection,
        ),
      );
      return true;
    },
    [clientToOriginalPoint, originalWidth, originalHeight],
  );

  const finishDetectionTransform = useCallback(() => {
    const edit = detectionEditRef.current;
    if (!edit) return false;

    detectionEditRef.current = null;
    if (edit.mode === "draw") {
      setEditableDetections((current) => {
        const box = current[edit.index]?.boundingBox;
        if (!box || box.x2 - box.x1 < 5 || box.y2 - box.y1 < 5) {
          setSelectedDetectionIndex(null);
          return current.filter((_, index) => index !== edit.index);
        }
        return current;
      });
      setIsDrawingDetection(false);
    }
    return true;
  }, []);

  const deleteSelectedDetection = useCallback(() => {
    if (selectedDetectionIndex == null) return;
    setEditableDetections((current) =>
      current.filter((_, index) => index !== selectedDetectionIndex),
    );
    setSelectedDetectionIndex(null);
  }, [selectedDetectionIndex]);

  const updateSelectedDetectionClass = useCallback(
    (className) => {
      if (selectedDetectionIndex == null) return;
      setEditableDetections((current) =>
        current.map((detection, index) =>
          index === selectedDetectionIndex
            ? { ...detection, cls: className }
            : detection,
        ),
      );
    },
    [selectedDetectionIndex],
  );

  const saveEditedDetections = useCallback(async () => {
    const imageId = selectedResult?.image_id || selectedImage?.id;
    if (!inspectionId || !imageId) {
      setDetectionEditError("Could not identify this image.");
      return;
    }

    try {
      setIsSavingDetections(true);
      setDetectionEditError("");
      const response = await axios.put(
        `/api/inspections/${inspectionId}/images/${imageId}/detections`,
        {
          detections: editableDetections.map((detection) => ({
            cls: detection.cls,
            confidence: detection.confidence ?? 1,
            boundingBox: detection.boundingBox,
          })),
        },
        {
          headers: {
            Authorization: `Bearer ${getAuthToken()}`,
          },
        },
      );

      setAnalysisData((current) => ({
        ...current,
        results: (current?.results || []).map((result) =>
          String(result.image_id) === String(imageId)
            ? { ...result, ...response.data }
            : result,
        ),
      }));
      setEditableDetections(cloneDetections(response.data?.detections || []));
      setSelectedDetectionIndex(null);
      setIsDrawingDetection(false);
      setIsEditingDetections(false);
    } catch (saveError) {
      setDetectionEditError(
        saveError.response?.data?.error ||
        saveError.response?.data?.message ||
        "Could not save detection changes.",
      );
    } finally {
      setIsSavingDetections(false);
    }
  }, [
    selectedResult,
    selectedImage,
    inspectionId,
    editableDetections,
    cloneDetections,
  ]);

  const clampModalOffset = useCallback(
    (x, y, zoom = modalZoom) => {
      const container = modalContainerRef.current;
      const img = modalImageRef.current;
      if (!container || !img) return { x, y };
      const cw = container.clientWidth;
      const ch = container.clientHeight;
      const scaledW = img.offsetWidth * zoom;
      const scaledH = img.offsetHeight * zoom;
      return {
        x: Math.min(0, Math.max(Math.min(0, cw - scaledW), x)),
        y: Math.min(0, Math.max(Math.min(0, ch - scaledH), y)),
      };
    },
    [modalZoom],
  );

  const handleModalWheel = useCallback(
    (e) => {
      e.preventDefault();

      const container = modalContainerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const cursorX = e.clientX - rect.left;
      const cursorY = e.clientY - rect.top;
      const next = Math.min(Math.max(modalZoom + e.deltaY * -0.001, 1), 10);

      if (next === modalZoom) return;
      if (next === 1) {
        setModalOffset({ x: 0, y: 0 });
        setModalZoom(next);
        return;
      }

      const imageX = (cursorX - modalOffset.x) / modalZoom;
      const imageY = (cursorY - modalOffset.y) / modalZoom;
      const nextX = cursorX - imageX * next;
      const nextY = cursorY - imageY * next;

      setModalOffset(clampModalOffset(nextX, nextY, next));
      setModalZoom(next);
    },
    [modalZoom, modalOffset, clampModalOffset],
  );

  const handleModalMouseDown = useCallback(
    (e) => {
      if (beginPointNote(e)) return;
      if (beginDrawingDetection(e)) return;
      if (modalZoom <= 1) return;
      modalDragRef.current = { active: true, lastX: e.clientX, lastY: e.clientY };
      setModalDragging(true);
    },
    [modalZoom, beginDrawingDetection, beginPointNote],
  );

  const handleModalMouseMove = useCallback(
    (e) => {
      updateThermalProbe(e);
      if (updateDetectionTransform(e)) return;
      if (!modalDragRef.current.active) return;
      const dx = e.clientX - modalDragRef.current.lastX;
      const dy = e.clientY - modalDragRef.current.lastY;
      modalDragRef.current.lastX = e.clientX;
      modalDragRef.current.lastY = e.clientY;
      setModalOffset((prev) =>
        clampModalOffset(prev.x + dx, prev.y + dy, modalZoom),
      );
    },
    [modalZoom, clampModalOffset, updateDetectionTransform, updateThermalProbe],
  );

  const handleModalMouseUp = useCallback(() => {
    finishDetectionTransform();
    modalDragRef.current.active = false;
    setModalDragging(false);
  }, [finishDetectionTransform]);

  const resetModalView = useCallback(() => {
    setModalZoom(1);
    setModalOffset({ x: 0, y: 0 });
  }, []);

  // Close modal with Escape
  useEffect(() => {
    if (!showImageModal) return;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (isEditingDetections) {
        cancelDetectionEditing();
      } else {
        closeImageModal();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    showImageModal,
    isEditingDetections,
    cancelDetectionEditing,
    closeImageModal,
  ]);

  // Measure the visible image area inside object-fit: contain.
  useEffect(() => {
    if (!showImageModal) return;
    const image = modalImageRef.current;
    const container = modalContainerRef.current;
    if (!image || !container) return;

    const update = () => {
      const naturalWidth = image.naturalWidth;
      const naturalHeight = image.naturalHeight;
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;

      if (!naturalWidth || !naturalHeight || !containerWidth || !containerHeight) {
        return;
      }

      const fitScale = Math.min(
        containerWidth / naturalWidth,
        containerHeight / naturalHeight,
      );
      const width = naturalWidth * fitScale;
      const height = naturalHeight * fitScale;

      setModalImageRect({
        left: (containerWidth - width) / 2,
        top: (containerHeight - height) / 2,
        width,
        height,
      });
      const previousView = comparisonViewportRef.current;
      if (previousView) {
        setModalOffset({
          x: containerWidth / 2 - ((containerWidth - width) / 2 + previousView.centerX * width) * previousView.zoom,
          y: containerHeight / 2 - ((containerHeight - height) / 2 + previousView.centerY * height) * previousView.zoom,
        });
        comparisonViewportRef.current = null;
      }
    };

    if (image.complete && image.naturalWidth > 0) {
      update();
    } else {
      image.addEventListener("load", update);
    }

    const ro = new ResizeObserver(update);
    ro.observe(container);

    return () => {
      image.removeEventListener("load", update);
      ro.disconnect();
    };
  }, [showImageModal, src, showRgbComparison]);

  /* ----------------------------- PDF ----------------------------- */

  async function downloadPdfReport(pdfPayload) {
    const res = await axios.post(`/api/reports/pdf`, pdfPayload, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getAuthToken()}`,
      },
      responseType: "blob",
    });

    const blob = new Blob([res.data], { type: "application/pdf" });
    const dispo = res.headers["content-disposition"] || "";
    const match = dispo.match(/filename="?([^"]+)"?/);
    const filename = match?.[1] || "report.pdf";

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  function exportCsv() {
    const minConf = confidenceThreshold / 100;

    const headers = [
      "Image", "Pole", "Line", "Lat", "Lon",
      "Total Detections", "Detections Detail",
    ];

    const rows = (results || []).map((r) => {
      const dets = (r.detections || []).filter((d) => (d.confidence ?? 1) >= minConf);
      const detail = dets
        .map((d) => `${d.cls}(${((d.confidence ?? 0) * 100).toFixed(0)}%)`)
        .join("; ");
      return [
        r.image_name ?? "",
        r.posteNombre ?? "",
        r.poleLineName ?? "",
        r.posteLat ?? r.imageLat ?? "",
        r.posteLong ?? r.imageLon ?? "",
        dets.length,
        detail,
      ];
    });

    const escape = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const csv = [headers, ...rows].map((row) => row.map(escape).join(",")).join("\n");

    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${inspectionName || inspectionId}_results.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  async function downloadImagesZip(filter) {
    try {
      setExportError(null);
      setIsDownloadingZip(true);

      const res = await axios.get(
        `/api/inspections/${inspectionId}/images/download-zip`,
        {
          params: { filter },
          headers: { Authorization: `Bearer ${getAuthToken()}` },
          responseType: "blob",
        }
      );

      const blob = new Blob([res.data], { type: "application/zip" });
      const safeInspectionName = String(inspectionName || inspectionId || "inspection")
        .trim()
        .replace(/[<>:"/\\|?*]/g, "_")
        .replace(/\s+/g, " ");
      const filename = `${safeInspectionName}.zip`;

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      setShowExportModal(false);
    } catch (e) {
      setExportError(e?.message || "Error downloading images");
    } finally {
      setIsDownloadingZip(false);
    }
  }

  const buildReportPayloadFromResults = useCallback(({
    results,
    pdfOption,
    selectedImageName,
    notesIndex,
  }) => {
    const sourceResults = isThermalInspection
      ? selectedImagesSafe.map((image) => ({
          image_id: getImgId(image),
          image_name: getImgName(image),
          image_path: image.imagePath || image.originalUrl || image.signedUrl || image.imageUrl || image.url,
          detections: [],
        }))
      : results;

    const items = (() => {
      const opt = (pdfOption || "").toLowerCase();
      const reportableResults = sourceResults.filter(
        (result) => !excludedReportImageKeys.has(reportImageKey(result.image_name)),
      );

      if (opt === "all") return reportableResults;
      if (opt === "detections" && !isThermalInspection) {
        return reportableResults.filter((result) => filteredDets(result).length > 0);
      }

      const b = normKey(selectedImageName);
      if (excludedReportImageKeys.has(b)) return [];
      return reportableResults.filter((r) => normKey(r.image_name) === b);
    })();

    const filtered = items.map((r) => ({
      ...r,
      detections: Array.isArray(r?.detections) ? r.detections : [],
    }));

    const images = filtered.map((r) => {
      const gcs = parseGcsFromPublicUrl(r?.image_path);
      if (!gcs)
        throw new Error(
          `Could not read the storage location from imagePath: ${r?.image_path}`,
        );
      const resultImageId = getResultImageId(r);

      return {
        imageName: r.image_name,
        bucket: gcs.bucket,
        objectPath: gcs.objectPath,
        resultLink: `${window.location.origin}/inspections/${inspectionId}/results${resultImageId ? `?imageId=${resultImageId}` : ""}`,
        gps:
          r.posteLat != null && r.posteLong != null
            ? { lat: r.posteLat, lon: r.posteLong }
            : r.imageLat != null && r.imageLon != null
              ? { lat: r.imageLat, lon: r.imageLon }
              : null,
        poleName: r.posteNombre,
        poleLineName: r.poleLineName,
        notes: notesForReportImage(r, notesIndex),
        detections: filteredDets(r).map((d) => ({
          cls: d.cls,
          confidence: d.confidence ?? 0,
          color: PALETTE?.[d.cls]?.bar ?? null,
          boundingBox: {
            x1: Math.round(d.boundingBox?.x1 ?? 0),
            y1: Math.round(d.boundingBox?.y1 ?? 0),
            x2: Math.round(d.boundingBox?.x2 ?? 0),
            y2: Math.round(d.boundingBox?.y2 ?? 0),
          },
        })),
      };
    });

    return { projectName, inspectionName, inspectionId, createdBy, createdAt, images };
  }, [
    createdAt,
    createdBy,
    excludedReportImageKeys,
    filteredDets,
    inspectionId,
    inspectionName,
    isThermalInspection,
    notesForReportImage,
    projectName,
    selectedImagesSafe,
  ]);

  const onGenerateReport = useCallback(async () => {
    try {
      setError(null);
      setIsGenerating(true);

      const selectedImageName = getImgName(
        selectedImagesSafe[selectedImageIndex],
      );
      if (!selectedImageName) throw new Error("No image selected");
      if (isSolarInspection) {
        const included = selectedImagesSafe.filter(image => !excludedReportImageKeys.has(reportImageKey(getImgName(image))));
        const scope = pdfOption === "current" ? included.filter(image => getImgId(image) === selectedImageId) : included;
        const imageIds = scope.map(getImgId).filter(Boolean);
        if (!imageIds.length) throw new Error("No included thermal images in the selected report scope.");
        if (!reportMapCapture.current) throw new Error("Wait for the Location Overview preview to load.");
        const locationOverview = await reportMapCapture.current();
        await downloadPdfReport({ inspectionId, locationOverview, solarReport: solarReportOptions(solarReportValues, imageIds, pdfOption === "detections") });
        return true;
      }
      const notesIndex = await ensureReportNotesLoaded({
        results: results || [],
        pdfOption,
        selectedImageName,
      });

      const pdfPayload = buildReportPayloadFromResults({
        results: results || [],
        pdfOption,
        selectedImageName,
        notesIndex,
      });

      if (!pdfPayload.images || pdfPayload.images.length === 0) {
        throw new Error(
          excludedReportImageKeys.has(selectedImageReportKey) && pdfOption === "current"
            ? "This image is excluded from the report. Include it again before generating a current-image report."
            : pdfOption === "detections"
            ? "No analyzed images with detections were found."
            : "No included analyzed images found. Include at least one image before generating a report.",
        );
      }

      if (!reportMapCapture.current) throw new Error("Wait for the Location Overview preview to load.");
      pdfPayload.locationOverview = await reportMapCapture.current();
      await downloadPdfReport(pdfPayload);
      return true;
    } catch (e) {
      let message = e?.response?.data?.message || e?.message || "Error generating PDF";
      if (e?.response?.data instanceof Blob) {
        try { const body = JSON.parse(await e.response.data.text()); message = body.message || body.detail || body.error || message; } catch { /* Keep the original error. */ }
      }
      setError(message);
      return false;
    } finally {
      setIsGenerating(false);
    }
  }, [pdfOption, results, selectedImagesSafe, selectedImageIndex, excludedReportImageKeys, selectedImageReportKey, ensureReportNotesLoaded, buildReportPayloadFromResults, isSolarInspection, inspectionId, selectedImageId, solarReportValues]);

  /* ----------------------------- Guards ----------------------------- */

  if (!inspectionId) return <div className="ir-page">Missing inspection ID.</div>;

  if (isLoadingResults && (!analysisData || results.length === 0)) {
    return (
      <div className="ir2-page ir2-loading-page">
        <LoadingOverlay show text="Loading results..." />
      </div>
    );
  }

  if (isSolarInspection && selectedImagesLocal.length > 0 && selectedImagesSafe.length === 0)
    return <div className="ir-page">No thermal images found. Upload images with filenames ending in _T to view results. RGB images are used only for comparison.</div>;

  if (!selectedImage || !src)
    return <div className="ir-page">No image selected.</div>;

  /* ----------------------------- Render ----------------------------- */

  const isPartial = !isThermalInspection && analysisData && !analysisData.done && results.length > 0;

  return (
    <div className="ir2-page">
      {/* Partial results banner */}
      {isPartial && (
        <div className="si-banner si-banner--warning">
          <span>
            Partial results: {analysisData.processed} of {analysisData.totalSelected} images analyzed.
            The report will include only the images analyzed so far.
          </span>
        </div>
      )}

      {/* Header */}
      <div className="ir2-head">
        <div>
          <div className="ir2-title">{isThermalInspection ? "Thermal Results" : "Image Results"}</div>
          <div className="ir2-subtitle">
            {projectName ? `${projectName} • ` : ""}
            {inspectionName || `Inspection ${inspectionId}`}
          </div>
        </div>

        <div className="ir2-head-actions">
          <button
            className="btn btn-outline"
            onClick={() => setShowExportModal(true)}
            disabled={isLoadingResults}
          >
            ⬇ Export
          </button>
          <button
            className="btn btn-success"
            onClick={() => setShowPdfModal(true)}
            disabled={isLoadingResults}
          >
            Generate Report
          </button>
        </div>
      </div>

      {isSolarInspection && <SolarAnalysisPanel analysis={solar} readOnly={isViewer} />}
      {/* Metrics */}
      <div className="ir2-metrics">
        <div className="ir2-card ir2-metric">
          <div className="ir2-metric-label">Total Images</div>
          <div className="ir2-metric-value">{totalImages}</div>
        </div>

        <div className="ir2-card ir2-metric">
          <div className="ir2-metric-label">{isThermalInspection ? "Thermal Images" : "Analyzed Images"}</div>
          <div className="ir2-metric-value">{analyzedImagesCount}</div>
        </div>

        <div className="ir2-card ir2-metric">
          <div className="ir2-metric-label">{isThermalInspection ? "AI Detections" : "Total Detections"}</div>
          <div className="ir2-metric-value">{totalDetections}</div>
        </div>

        {!isSolarInspection && <div className="ir2-card ir2-metric">
          <div className="ir2-metric-label">Poles w/ Images</div>
          <div className="ir2-metric-value">{polesWithImages}</div>
        </div>}
      </div>

      {/* Main grid */}
      <div className="ir2-grid">
        {/* LEFT */}
        <div className="ir2-card ir2-left">
          <div className="ir2-list-head">
            <div className="ir2-list-title-row">
              <div className="ir2-list-title">{resultsView === "map" ? "Photo Map" : "Image List"}</div>
              <div className="ir2-tabs" aria-label="Results view">
                <button className={`ir2-tab ${resultsView === "list" ? "is-active" : ""}`} aria-pressed={resultsView === "list"} onClick={() => setResultsView("list")}>List</button>
                <button className={`ir2-tab ${resultsView === "map" ? "is-active" : ""}`} aria-pressed={resultsView === "map"} onClick={() => setResultsView("map")}>Map</button>
              </div>
              {isElectricNetworkInspection && (
                <button
                  className={`ir2-group-toggle ${groupMode === "line" ? "is-active" : ""}`}
                  onClick={() => setGroupMode((m) => m === "flat" ? "line" : "flat")}
                  title="Group by line"
                >
                  ⣿ Group by line
                </button>
              )}
            </div>

            <div className="ir2-list-controls">
              <div className="ir2-report-summary" aria-label="Report inclusion summary">
                <span>
                  <strong>{includedReportCount}</strong> included
                </span>
                <span>
                  <strong>{excludedReportCount}</strong> excluded
                </span>
              </div>

              {hasAnalysisModel && (
                <>
                  <div className="ir2-tabs">
                    <button
                      className={`ir2-tab ${listFilter === "all" ? "is-active" : ""}`}
                      onClick={() => setListFilter("all")}
                    >
                      All ({reportScopedRows.length})
                    </button>
                    <button
                      className={`ir2-tab ${listFilter === "with" ? "is-active" : ""}`}
                      onClick={() => setListFilter("with")}
                    >
                      With Detections ({reportScopedRows.filter((r) => r.hasDet).length})
                    </button>
                    <button
                      className={`ir2-tab ${listFilter === "none" ? "is-active" : ""}`}
                      onClick={() => setListFilter("none")}
                    >
                      No Detections ({reportScopedRows.filter((r) => !r.hasDet).length})
                    </button>
                  </div>

                  <select
                    className="ir2-select"
                    value={selectedClass}
                    onChange={(e) => setSelectedClass(e.target.value)}
                  >
                    <option value="">Show All Classes</option>
                    {detectionsByClass.map((c) => (
                      <option key={c.name} value={c.name}>
                        {c.name}
                      </option>
                    ))}
                  </select>

                  <div className="ir2-confidence">
                    <span className="ir2-confidence-label">
                      Min confidence: <strong>{confidenceThreshold}%</strong>
                    </span>
                    <input
                      type="range"
                      min={0}
                      max={95}
                      step={5}
                      value={confidenceThreshold}
                      onChange={(e) => setConfidenceThreshold(Number(e.target.value))}
                      className="ir2-confidence-slider"
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {resultsView === "map" ? (
            <ResultsMap inspectionId={inspectionId} isSolarInspection={isSolarInspection} readOnly={isViewer} analysisRefresh={solar.detail} status={[mapLoadStatus, gpsStatus].filter(Boolean).join(" ")} key={inspectionId} onSelect={(index) => { if (canOpenImage(getImgId(selectedImagesSafe[index]))) setSelectedImageIndex(index); }} rows={classFilteredRows} selectedIndex={selectedImageIndex} layers={mapLayers} setLayers={setMapLayers} onOpen={openImageModal} />
          ) : groupMode === "flat" ? (
            <>
              <div className={`ir2-table ${!hasAnalysisModel ? "is-compact" : ""}`}>
                <div className={`ir2-tr ir2-th ${!hasAnalysisModel ? "ir2-tr-compact" : ""}`}>
                  <div>Image</div>
                  <div>{isElectricNetworkInspection ? "Pole" : "Active"}</div>
                  {hasAnalysisModel ? (
                    <>
                      <div>Detections</div>
                      <div>Detected Classes</div>
                    </>
                  ) : (
                    <div>Notes</div>
                  )}
                </div>

                {pagedRows.map((row) => (
                  <ImageRow
                    key={`${row.name}-${row.idx}`}
                    row={row}
                    isSelected={row.idx === selectedImageIndex}
                    onClick={() => { if (row.canOpen) { if (isSolarInspection) openImageModal(row.idx); else setSelectedImageIndex(row.idx); } }}
                    onRetry={!isViewer && row.analysisTaskId ? () => solar.retry(row.analysisTaskId) : undefined}
                    onToggleReportInclude={toggleReportInclude}
                    compactMode={!hasAnalysisModel}
                    showPoleColumn={isElectricNetworkInspection}
                    onOpenNotes={() => {
                      setPendingNoteAnnotation(null);
                      setNoteLabelDraft("");
                      setShowNotesModal(true);
                    }}
                  />
                ))}
              </div>

              {/* Pagination */}
              <div className="ir2-pagination">
                <button className="ir2-pagebtn" disabled={page === 1} onClick={() => setPage(1)}>«</button>
                <button className="ir2-pagebtn" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>‹</button>
                <span className="ir2-pagelabel">Page {page} / {totalPages}</span>
                <button className="ir2-pagebtn" disabled={page === totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>›</button>
                <button className="ir2-pagebtn" disabled={page === totalPages} onClick={() => setPage(totalPages)}>»</button>
              </div>
            </>
          ) : (
            <div className="ir2-grouped">
              {groupedRows.map((group) => {
                const isCollapsed = collapsedGroups.has(group.lineName);
                const hasDetInGroup = group.totalDet > 0;
                return (
                  <div key={group.lineName} className="ir2-group">
                    <div
                      className="ir2-group-header"
                      onClick={() => toggleGroup(group.lineName)}
                    >
                      <span className="ir2-group-chevron">{isCollapsed ? "▶" : "▼"}</span>
                      <span className="ir2-group-name">{group.lineName}</span>
                      <span className="ir2-group-stats">
                        {group.rows.length} images
                        {hasDetInGroup && (
                          <span className="ir2-group-detbadge">{group.totalDet} det.</span>
                        )}
                      </span>
                    </div>

                    {!isCollapsed && (
                      <div className="ir2-group-rows">
                        <div className={`ir2-tr ir2-th ir2-group-th ${!hasAnalysisModel ? "ir2-tr-compact" : ""}`}>
                          <div>Image</div>
                          <div>{isElectricNetworkInspection ? "Pole" : "Active"}</div>
                          {hasAnalysisModel ? (
                            <>
                              <div>Detections</div>
                              <div>Detected Classes</div>
                            </>
                          ) : (
                            <div>Notes</div>
                          )}
                        </div>
                        {group.rows.map((row) => (
                          <ImageRow
                            key={`${row.name}-${row.idx}`}
                            row={row}
                            isSelected={row.idx === selectedImageIndex}
                            onClick={() => { if (row.canOpen) { if (isSolarInspection) openImageModal(row.idx); else setSelectedImageIndex(row.idx); } }}
                    onRetry={!isViewer && row.analysisTaskId ? () => solar.retry(row.analysisTaskId) : undefined}
                            onToggleReportInclude={toggleReportInclude}
                            compactMode={!hasAnalysisModel}
                            showPoleColumn={isElectricNetworkInspection}
                            onOpenNotes={() => {
                              setPendingNoteAnnotation(null);
                              setNoteLabelDraft("");
                              setShowNotesModal(true);
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* RIGHT */}
        <div className="ir2-right">
          {hasAnalysisModel && (
            <div className="ir2-card ir2-chart">
              <div className="ir2-section-title">Detections by Class</div>
              <div className="ir2-chart-body">
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={detectionsByClass.slice(0, 8)}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={55}
                      outerRadius={85}
                    >
                      {detectionsByClass.slice(0, 8).map((entry) => (
                        <Cell
                          key={`cell-${entry.name}`}
                          fill={getClassColor(entry.name)}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      content={
                        <DetectionClassTooltip
                          total={detectionsByClass.reduce(
                            (sum, entry) => sum + Number(entry.value || 0),
                            0,
                          )}
                        />
                      }
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          <div className="ir2-card ir2-preview">
            <div className="ir2-preview-head">
              <div className="ir2-section-title">Preview</div>
              <div className="ir2-preview-actions">
                <button
                  className="btn btn-outline"
                  disabled={!canViewSelected}
                  onClick={() => {
                    setPendingNoteAnnotation(null);
                    setNoteLabelDraft("");
                    setShowNotesModal(true);
                  }}
                >
                  <FontAwesomeIcon icon={faPen} />
                  <span>Add notes</span>
                  {selectedImageNotes.length > 0 && (
                    <span className="ir2-notes-count">{selectedImageNotes.length}</span>
                  )}
                </button>
              </div>
            </div>

              {canViewSelected ? <div
                className="ir2-stage image-container"
                ref={imageContainerRef}
                onWheel={handleZoom}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              >
                <div
                  className="ir-stage-inner"
                  style={{
                    transform: `translate(${imageOffset.x}px, ${imageOffset.y}px) scale(${zoomLevel})`,
                    transformOrigin: "top left",
                    cursor:
                      zoomLevel > 1
                        ? dragging
                          ? "grabbing"
                          : "grab"
                        : "default",
                  }}
                >
                  <img
                    ref={imageRef}
                    src={src ?? ""}
                    crossOrigin="anonymous"
                    alt={`Selected ${selectedImageIndex}`}
                    className="ir-img"
                    onLoad={measurePreviewImage}
                  />

                  {readyForBoxes &&
                    boundingBoxes.map((detection, i) => {
                      const scaleX = imageWidth / originalWidth;
                      const scaleY = imageHeight / originalHeight;

                      const x1 = imageLeft + (detection.boundingBox?.x1 ?? 0) * scaleX;
                      const y1 = imageTop + (detection.boundingBox?.y1 ?? 0) * scaleY;
                      const width =
                        ((detection.boundingBox?.x2 ?? 0) -
                          (detection.boundingBox?.x1 ?? 0)) *
                        scaleX;
                      const height =
                        ((detection.boundingBox?.y2 ?? 0) -
                          (detection.boundingBox?.y1 ?? 0)) *
                        scaleY;

                      const borderColor = CLASS_TO_BOX_COLOR(detection.cls);
                      const fontSize = `${Math.max(10, 10 * zoomLevel)}px`;
                      return (
                        <React.Fragment key={i}>
                          <div
                            style={{
                              position: "absolute",
                              left: `${x1}px`,
                              top: `${y1}px`,
                              width: `${width}px`,
                              height: `${height}px`,
                              border: `2px solid ${borderColor}`,
                              zIndex: 10,
                              pointerEvents: "none",
                            }}
                          />
                          <div
                            style={{
                              position: "absolute",
                              left: `${x1}px`,
                              top: `${Math.max(0, y1 - 18)}px`,
                              backgroundColor: `${borderColor}80`,
                              color: "white",
                              padding: "2px 6px",
                              borderRadius: "6px",
                              fontSize,
                              fontWeight: 700,
                              whiteSpace: "nowrap",
                              zIndex: 11,
                              pointerEvents: "none",
                            }}
                          >
                            {detection.cls} (
                            {((detection.confidence ?? 0) * 100).toFixed(1)}%)
                          </div>
                        </React.Fragment>
                      );
                    })}
                </div>

                <button
                  className="ir-reset"
                  onClick={resetView}
                  disabled={isCentered}
                  title="Reset view"
                >
                  <FontAwesomeIcon icon={faRotateRight} />
                </button>

                <button
                  className="ir-expand-btn"
                  onClick={openImageModal}
                  title="Expand image"
                >
                  <FontAwesomeIcon icon={faExpand} />
                </button>
              </div>
              : <div className="ir2-stage solar-preview-locked" role="status">Select a DONE image to open the preview.</div>}
            <div className="ir2-meta">
              <div className="ir2-meta-row">
                <span className="ir2-meta-k">Image:</span>
                <span className="ir2-meta-v">{selName}</span>
              </div>
              <div className="ir2-meta-row">
                <span className="ir2-meta-k">GPS:</span>
                <span className="ir2-meta-v">
                  {coordsToShow
                    ? `${Number(coordsToShow.lat).toFixed(6)}, ${Number(coordsToShow.lon).toFixed(6)}`
                    : "—"}
                </span>
              </div>
              <div className="ir2-meta-row">
                <span className="ir2-meta-k">Detections:</span>
                <span className="ir2-meta-v">
                  {selectedResult?.total_detections ?? 0}
                </span>
              </div>
            </div>

          </div>

        </div>
      </div>

      {showNotesModal && (
        <div
          className="ir-modal-overlay ir-notes-overlay"
          onClick={() => {
            setShowNotesModal(false);
            setPendingNoteAnnotation(null);
            setNoteLabelDraft("");
          }}
        >
          <div className="ir-modal ir-notes-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ir-modal-header">
              <h3>Image notes</h3>
              <button
                onClick={() => {
                  setShowNotesModal(false);
                  setPendingNoteAnnotation(null);
                  setNoteLabelDraft("");
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="ir-modal-content ir-notes-modal-content">
              <div className="ir-notes-modal-image">{selName}</div>
              {pendingNoteAnnotation?.annotationType === "POINT" && (
                <div className="ir2-note-anchor">
                  Point note at {Math.round((pendingNoteAnnotation.xNorm || 0) * 100)}%, {Math.round((pendingNoteAnnotation.yNorm || 0) * 100)}%
                  {Number.isFinite(pendingNoteAnnotation.temperatureC)
                    ? ` · ${Number(pendingNoteAnnotation.temperatureC).toFixed(1)}°C`
                    : ""}
                </div>
              )}

              <div className="ir2-notes-list">
                {selectedImageNotes.length === 0 ? (
                  <div className="ir2-notes-empty">No notes for this image.</div>
                ) : (
                  selectedImageNotes.map((note) => (
                    <div className="ir2-note" key={note.id}>
                      <p>{note.note}</p>
                      <span>
                        {note.annotationType === "POINT"
                          ? `${note.label || "Point note"} · `
                          : ""}
                        {note.authorName || note.authorEmail || "Unknown user"}
                      </span>
                    </div>
                  ))
                )}
              </div>

              <div className="ir2-note-form">
                <textarea
                  value={noteDraft}
                  onChange={(event) => setNoteDraft(event.target.value)}
                  placeholder="Add a note for the report"
                  maxLength={2000}
                  disabled={!selectedImageId || isSavingNote}
                />
                {pendingNoteAnnotation?.annotationType === "POINT" && (
                  <input
                    className="ir2-note-label-input"
                    value={noteLabelDraft}
                    onChange={(event) => setNoteLabelDraft(event.target.value)}
                    placeholder="Marker name (optional)"
                    maxLength={80}
                    disabled={!selectedImageId || isSavingNote}
                  />
                )}
                {notesError && <div className="ir2-note-error">{notesError}</div>}
                {!selectedImageId && !notesError && (
                  <div className="ir2-note-error">Could not identify this image for notes.</div>
                )}
                <button
                  className="btn btn-primary"
                  onClick={submitNote}
                  disabled={!noteDraft.trim() || !selectedImageId || isSavingNote}
                >
                  {isSavingNote ? "Saving..." : "Add Note"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div className="ir-modal-overlay" onClick={() => setShowExportModal(false)}>
          <div className="ir-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ir-modal-header">
              <h3>Export</h3>
              <button onClick={() => setShowExportModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="ir-modal-content">
              <p style={{ margin: 0, fontSize: 13, color: "var(--app-text-muted)" }}>
                Select which images to include in the ZIP download:
              </p>
              <label className="radioInputLabel">
                <input
                  type="radio"
                  value="all"
                  checked={exportFilter === "all"}
                  onChange={(e) => setExportFilter(e.target.value)}
                />
                {isSolarInspection ? "All thermal images" : "All analyzed images"}
              </label>
              <label className="radioInputLabel">
                <input
                  type="radio"
                  value="detections"
                  checked={exportFilter === "detections"}
                  onChange={(e) => setExportFilter(e.target.value)}
                />
                Only images with detections
              </label>
              <p style={{ margin: 0, fontSize: 12, color: "var(--app-text-muted)", borderTop: "1px solid var(--app-border)", paddingTop: 8 }}>
                ⓘ Images with detections include bounding boxes drawn on them.
                Images without detections are included as originals.
              </p>

              {exportError && (
                <div style={{ color: "crimson", fontSize: 13 }}>{exportError}</div>
              )}
            </div>

            <div className="modalButtonsContainer">
              <button
                className="btn btn-outline"
                onClick={() => setShowExportModal(false)}
                disabled={isDownloadingZip}
              >
                Cancel
              </button>
              <button
                className="btn btn-outline"
                onClick={() => { exportCsv(); setShowExportModal(false); }}
                disabled={isDownloadingZip}
              >
                Export CSV
              </button>
              <button
                className="btn btn-success"
                onClick={() => downloadImagesZip(exportFilter)}
                disabled={isDownloadingZip}
              >
                {isDownloadingZip ? "Downloading…" : "Download ZIP"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Fullscreen image modal ── */}
      {showImageModal && canViewSelected && (
        <div
          className="ir-imgmodal-overlay"
          onClick={closeImageModal}
        >
          <div
            className="ir-imgmodal"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Toolbar */}
            <div className="ir-imgmodal-toolbar">
              <span className="ir-imgmodal-name">{selName}</span>

              <div className="ir-imgmodal-actions">
                {isSolarInspection && isSelectedThermal && <button className={`ir-imgmodal-btn ${showRgbComparison ? "is-active" : ""}`} aria-pressed={showRgbComparison} onClick={() => { comparisonViewportRef.current = thermalViewport(modalImageRect, modalZoom, modalOffset); setShowRgbComparison(value => !value); }}>Compare with RGB</button>}
                {isSolarInspection && isSelectedThermal && <>
                  <button className={`ir-imgmodal-btn ${addingManualAnomaly ? "is-active" : ""}`} disabled={solar.busy || !solar.detail} aria-pressed={addingManualAnomaly} onClick={()=>{setSelectingAnomalies(false);setSelectingThermalArea(false);setIsAddingPointNote(false);setShowAnomalies(true);setAddingManualAnomaly(v=>!v);}}>{addingManualAnomaly ? 'Cancel manual anomaly' : 'Add manual anomaly'}</button>
                  <button className={`ir-imgmodal-btn ${showAnomalies ? "is-active" : ""}`} aria-pressed={showAnomalies} onClick={() => { setShowAnomalies(value => !value); setSelectingAnomalies(false); }}>{showAnomalies ? "Hide anomalies" : "Show anomalies"}</button>
                  {!isViewer && <button className={`ir-imgmodal-btn ${selectingAnomalies ? "is-active" : ""}`} disabled={solar.busy || !solarCandidates.length} aria-pressed={selectingAnomalies} onClick={() => { setSelectingAnomalies(value => !value); setShowAnomalies(true); setSelectingThermalArea(false); setIsAddingPointNote(false); setAddingManualAnomaly(false); setThermalProbe(null); }}>Select anomalies by area</button>}
                </>}
                {isSelectedThermal && <>
                  <button className={`ir-imgmodal-btn ${showThermalExtrema ? "is-active" : ""}`} aria-pressed={showThermalExtrema} onClick={() => setShowThermalExtrema(value => !value)}>Image min / max</button>
                  <button className={`ir-imgmodal-btn ${selectingThermalArea ? "is-active" : ""}`} aria-pressed={selectingThermalArea} onClick={() => { setSelectingThermalArea(value => !value); setSelectingAnomalies(false); setIsAddingPointNote(false); setAddingManualAnomaly(false); setThermalProbe(null); }}>Select temperature area</button>
                </>}
                {isSelectedThermal && (
                  <button
                    className={`ir-imgmodal-btn ${isAddingPointNote ? "is-active" : ""}`}
                    onClick={startPointNote}
                    title="Add a note anchored to a point"
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    <span>Add point note</span>
                  </button>
                )}

                {!isViewer && !isThermalInspection && !isEditingDetections ? (
                  <button
                    className="ir-imgmodal-btn"
                    onClick={startDetectionEditing}
                    title="Edit detections"
                  >
                    <FontAwesomeIcon icon={faPen} />
                    <span>Edit detections</span>
                  </button>
                ) : !isViewer && !isThermalInspection ? (
                  <>
                    <button
                      className={`ir-imgmodal-btn ${isDrawingDetection ? "is-active" : ""}`}
                      onClick={() => {
                        setIsDrawingDetection((current) => !current);
                        setSelectedDetectionIndex(null);
                      }}
                      title="Draw a new bounding box"
                    >
                      <FontAwesomeIcon icon={faPlus} />
                      <span>Add box</span>
                    </button>

                    <select
                      className="ir-imgmodal-class-select"
                      value={
                        selectedDetectionIndex == null
                          ? ""
                          : editableDetections[selectedDetectionIndex]?.cls || ""
                      }
                      onChange={(event) =>
                        updateSelectedDetectionClass(event.target.value)
                      }
                      disabled={selectedDetectionIndex == null}
                      aria-label="Detection class"
                    >
                      <option value="">Select a box</option>
                      {availableDetectionClasses.map((className) => (
                        <option key={className} value={className}>
                          {className}
                        </option>
                      ))}
                    </select>

                    <button
                      className="ir-imgmodal-btn ir-imgmodal-btn-danger"
                      onClick={deleteSelectedDetection}
                      disabled={selectedDetectionIndex == null}
                      title="Delete selected bounding box"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>

                    <button
                      className="ir-imgmodal-btn"
                      onClick={cancelDetectionEditing}
                      disabled={isSavingDetections}
                    >
                      Cancel
                    </button>

                    <button
                      className="ir-imgmodal-btn ir-imgmodal-save"
                      onClick={saveEditedDetections}
                      disabled={isSavingDetections}
                    >
                      <FontAwesomeIcon icon={faFloppyDisk} />
                      <span>{isSavingDetections ? "Saving..." : "Save"}</span>
                    </button>
                  </>
                ) : null}

                {!isThermalInspection && (
                  <button
                    className={`ir-imgmodal-btn ${showBBoxInModal ? "is-active" : ""}`}
                    onClick={() => setShowBBoxInModal((v) => !v)}
                    title={showBBoxInModal ? "Hide detections" : "Show detections"}
                  >
                    <FontAwesomeIcon icon={showBBoxInModal ? faEye : faEyeSlash} />
                    <span>{showBBoxInModal ? "Hide detections" : "Show detections"}</span>
                  </button>
                )}

                {/* Reset view */}
                <button
                  className="ir-imgmodal-btn"
                  onClick={resetModalView}
                  title="Reset view"
                >
                  <FontAwesomeIcon icon={faRotateRight} />
                </button>

                {/* Close */}
                <button
                  className="ir-imgmodal-btn ir-imgmodal-close"
                  onClick={closeImageModal}
                  title="Close (Esc)"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            </div>

            {addingManualAnomaly && <div role="status" style={{padding:'8px 16px',background:'var(--app-media-bg, #0e1625)',color:'#e5e7eb',fontSize:13}}>{solar.busy ? 'Saving manual anomaly…' : 'Drag a rectangle around the anomaly. It will be saved to this image’s current analysis for review.'}</div>}
            {detectionEditError && (
              <div className="ir-imgmodal-edit-error">{detectionEditError}</div>
            )}

            <div className={`ir-imgmodal-body ${showRgbComparison ? "is-comparing" : ""}`}>
            {/* Stage */}
            <div
              className={`ir-imgmodal-stage ${isDrawingDetection || isAddingPointNote ? "is-drawing" : ""}`}
              ref={modalContainerRef}
              onWheel={handleModalWheel}
              onMouseDown={handleModalMouseDown}
              onMouseMove={handleModalMouseMove}
              onMouseUp={handleModalMouseUp}
              onMouseLeave={handleModalMouseUp}
            >
              <div
                style={{
                  transform: `translate(${modalOffset.x}px, ${modalOffset.y}px) scale(${modalZoom})`,
                  transformOrigin: "top left",
                  position: "absolute",
                  inset: 0,
                  cursor: isDrawingDetection || isAddingPointNote
                    ? "crosshair"
                    : modalZoom > 1
                      ? (modalDragging ? "grabbing" : "grab")
                      : "default",
                }}
              >
                <img
                  ref={modalImageRef}
                  src={src ?? ""}
                  crossOrigin="anonymous"
                  alt="fullscreen preview"
                  className="ir-imgmodal-img"
                />

                {showAnomalies && <SolarCandidateBoxes candidates={solarCandidates} selectedIds={selectedAnomalyIds} selectedId={solar.selectedCandidateId} rect={modalImageRect} />}
                {addingManualAnomaly && !solar.busy && <AnomalySelection rect={modalImageRect} onArea={async box=>{if(await solar.addManual(box))setAddingManualAnomaly(false);}} />}
                {selectingAnomalies && !solar.busy && <AnomalySelection rect={modalImageRect} candidates={solarCandidates} onSelect={ids => { setSelectedAnomalyIds(current => new Set([...current, ...ids])); setSelectingAnomalies(false); }} />}
                {isSelectedThermal && <ThermalExtrema key={selectedImageId} inspectionId={inspectionId} imageId={selectedImageId} rect={modalImageRect} showGlobal={showThermalExtrema} selecting={selectingThermalArea} onSelected={() => setSelectingThermalArea(false)} />}
                {isSelectedThermal &&
                  originalWidth > 0 && originalHeight > 0 &&
                  modalImageRect.width > 0 && modalImageRect.height > 0 &&
                  selectedImageNotes
                    .filter((note) => note.annotationType === "POINT")
                    .map((note, index) => {
                      const x = modalImageRect.left + (Number(note.xNorm) || 0) * modalImageRect.width;
                      const y = modalImageRect.top + (Number(note.yNorm) || 0) * modalImageRect.height;
                      const temp = Number.isFinite(note.temperatureC)
                        ? `${Number(note.temperatureC).toFixed(1)}°C`
                        : null;
                      const label = note.label || String(index + 1);
                      return (
                        <div
                          key={note.id}
                          className="ir-thermal-point-note"
                          style={{ left: x, top: y }}
                          title={`${label}${temp ? ` · ${temp}` : ""} · ${note.note || ""}`}
                        >
                          <span>{label}</span>
                        </div>
                      );
                    })}

                {/* Bounding boxes */}
                {showBBoxInModal &&
                  originalWidth > 0 && originalHeight > 0 &&
                  modalImageRect.width > 0 && modalImageRect.height > 0 &&
                  modalDetections.map((detection, i) => {
                    const sx = modalImageRect.width / originalWidth;
                    const sy = modalImageRect.height / originalHeight;
                    const x1 = modalImageRect.left + (detection.boundingBox?.x1 ?? 0) * sx;
                    const y1 = modalImageRect.top + (detection.boundingBox?.y1 ?? 0) * sy;
                    const w = ((detection.boundingBox?.x2 ?? 0) - (detection.boundingBox?.x1 ?? 0)) * sx;
                    const h = ((detection.boundingBox?.y2 ?? 0) - (detection.boundingBox?.y1 ?? 0)) * sy;
                    const color = CLASS_TO_BOX_COLOR(detection.cls);
                    const selected =
                      isEditingDetections && selectedDetectionIndex === i;
                    return (
                      <React.Fragment key={`${detection.cls}-${i}`}>
                        <div
                          className={`ir-editable-box ${selected ? "is-selected" : ""}`}
                          style={{
                            left: x1,
                            top: y1,
                            width: w,
                            height: h,
                            borderColor: color,
                            pointerEvents: isEditingDetections ? "auto" : "none",
                          }}
                          onMouseDown={(event) =>
                            beginDetectionTransform(event, i, "move")
                          }
                        >
                          {selected &&
                            ["nw", "ne", "sw", "se"].map((handle) => (
                              <span
                                key={handle}
                                className={`ir-edit-handle ir-edit-handle-${handle}`}
                                onMouseDown={(event) =>
                                  beginDetectionTransform(
                                    event,
                                    i,
                                    "resize",
                                    handle,
                                  )
                                }
                              />
                            ))}
                        </div>
                        <div style={{
                          position: "absolute",
                          left: x1, top: Math.max(0, y1 - 22),
                          background: `${color}cc`,
                          color: "var(--app-text-on-accent)",
                          padding: "2px 8px",
                          borderRadius: 6,
                          fontSize: 13,
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                          pointerEvents: "none", zIndex: 11,
                        }}>
                          {detection.cls}
                          {!isEditingDetections &&
                            ` (${((detection.confidence ?? 0) * 100).toFixed(1)}%)`}
                        </div>
                      </React.Fragment>
                    );
                  })}
              </div>
              {isSelectedThermal && thermalProbe && !selectingThermalArea && !selectingAnomalies && !showNotesModal && (
                <div
                  className="ir-thermal-probe"
                  style={{
                    left: Math.min(thermalProbe.x + 14, Math.max(14, (modalContainerRef.current?.clientWidth || 0) - 220)),
                    top: Math.max(12, thermalProbe.y + 14),
                  }}
                >
                  {thermalProbe.loading
                    ? "Checking temperature..."
                    : Number.isFinite(thermalProbe.temperatureC)
                      ? `${Number(thermalProbe.temperatureC).toFixed(1)}°C`
                      : thermalProbe.message || "Temperature unavailable"}
                </div>
              )}
            </div>
            {showRgbComparison && isSelectedThermal && <>
              {comparisonError ? <p role="alert">{comparisonError}</p> : null}
              <RgbComparison viewport={thermalViewport(modalImageRect, modalZoom, modalOffset)} images={rgbImages} selectedId={automaticRgbId} />
            </>}
            {isSolarInspection && isSelectedThermal && <SolarReviewSidebar key={selectedImageId} analysis={solar} readOnly={isViewer} canReview selectedIds={selectedAnomalyIds}
              onToggle={id => { setShowAnomalies(true); setSelectedAnomalyIds(current => { const next = new Set(current); if (next.has(id)) next.delete(id); else next.add(id); return next; }); }}
              onDelete={() => solar.reviewMany(solarCandidates.filter(candidate => selectedAnomalyIds.has(candidate.id) && candidate.reviewStatus !== 'DISMISSED'), 'DISMISSED')}
              onFocus={(candidate) => {
              const stage = modalContainerRef.current;
              if (!stage || modalImageRect.width <= 0) return;
              const view = candidateViewport(candidate.box, modalImageRect, stage.clientWidth, stage.clientHeight);
              setShowAnomalies(true); setSelectingAnomalies(false);
              setModalZoom(view.zoom); setModalOffset({x:view.x,y:view.y});
              setSelectingThermalArea(false); setIsAddingPointNote(false); setAddingManualAnomaly(false); setThermalProbe(null);
            }} />}
            </div>
          </div>
        </div>
      )}

      {/* PDF Modal */}
      {showPdfModal && (
        <div
          className="ir-modal-overlay"
          onClick={() => !isGenerating && setShowPdfModal(false)}
        >
          <div className="ir-modal solar-report-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ir-modal-header">
              <h3>Generate Report PDF</h3>
              <button onClick={() => !isGenerating && setShowPdfModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="ir-modal-content">
              <label className="radioInputLabel">
                <input
                  type="radio"
                  value="current"
                  checked={pdfOption === "current"}
                  onChange={(e) => setPdfOption(e.target.value)}
                />
                {isSolarInspection ? "Current thermal image" : "Current image only"}
              </label>
              <label className="radioInputLabel">
                <input
                  type="radio"
                  value="all"
                  checked={pdfOption === "all"}
                  onChange={(e) => setPdfOption(e.target.value)}
                />
                {isSolarInspection ? "All thermal images" : "All analyzed images"}
              </label>
              <label className="radioInputLabel">
                <input
                  type="radio"
                  value="detections"
                  checked={pdfOption === "detections"}
                  onChange={(e) => setPdfOption(e.target.value)}
                />
                {isSolarInspection ? "Images with reportable findings" : "Images with detections only"}
              </label>

              {isSolarInspection && <SolarReportFields inspectionId={inspectionId} imageId={selectedImageId} values={solarReportValues} onChange={setSolarReportValues} disabled={isGenerating} />}
              {<SolarReportMap inspectionId={inspectionId} layers={mapLayers} rows={classFilteredRows} status={mapLoadStatus} captureRef={reportMapCapture} disabled={isGenerating} />}
              {error && (
                <div role="alert" style={{ color: "crimson", marginTop: 10 }}>{error}</div>
              )}
            </div>
            <div className="modalButtonsContainer">
              <button
                className="btn btn-outline w-full"
                onClick={() => !isGenerating && setShowPdfModal(false)}
                disabled={isGenerating}
              >
                Cancel
              </button>

              <button
                className="btn btn-primary w-full"
                disabled={isGenerating}
                onClick={async () => {
                  if (await onGenerateReport()) setShowPdfModal(false);
                }}
              >
                {isGenerating ? "Generating..." : "Generate"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
